import { apiClient } from './client';

// ─── Shared Types ──────────────────────────────────────────
export interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
}

export interface SingleResponse<T> {
  data: T;
}

interface ListParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  keyword?: string;
  [key: string]: unknown;
}

// ─── Auth ──────────────────────────────────────────────────
export interface LoginResult {
  accessToken: string;
}

export interface UserProfile {
  id: string;
  username: string;
  email: string;
  displayName: string;
  role: string;
  phone: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  inventoryAccessScope: 'GLOBAL' | 'WAREHOUSES';
  inventoryWarehouseIds: string[];
}

export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post<LoginResult>('/auth/login', { email, password }),

  getProfile: () =>
    apiClient.get<SingleResponse<UserProfile>>('/auth/profile'),

  updateProfile: (data: Partial<Pick<UserProfile, 'displayName' | 'phone'>>) =>
    apiClient.patch<SingleResponse<UserProfile>>('/auth/profile', data),
};

// ─── M1 Product ────────────────────────────────────────────
export interface Product {
  id: string;
  slug: string;
  nameEn: string;
  nameZh: string | null;
  descriptionEn: string | null;
  descriptionZh: string | null;
  productLine: string;
  categoryId: string | null;
  modelNumber: string | null;
  brand: string | null;
  supplierId: string | null;
  status: string;
  specsJson: Record<string, unknown> | null;
  coverImageId?: string | null;
  imageIds?: string[] | null;
  videoUrl?: string | null;
  translationsJson?: Record<string, { name: string; description?: string; features?: string[] }> | null;
  featuresJson?: { zh?: string; en: string }[] | null;
  documentsJson?: { name: string; url: string; type?: string }[] | null;
  isFeatured: boolean;
  sortOrder: number;
  seoTitle: string | null;
  seoDescription: string | null;
  // Pricing fields
  listPrice?: number | null;
  costPrice?: number | null;
  discountRate?: number | null;
  currency?: string;
  minOrderQty?: number | null;
  leadTimeDays?: number | null;
  adminNotes?: string | null;
  // OPT-08: Dynamic pricing & availability
  priceTierJson?: Array<{ minQty: number; maxQty?: number; price: number; currency?: string }> | null;
  regionalPriceJson?: Record<string, number> | null;
  stockStatus?: string;
  moq?: number | null;
  // OPT-12: Lifecycle
  lifecycleStage?: string | null;
  lifecycleNotes?: string | null;
  discontinuedAt?: string | null;
  // OPT-06: Industry application tags
  industriesJson?: string[] | null;
  processesJson?: string[] | null;
  materialsJson?: string[] | null;
  complianceMarketsJson?: string[] | null;
  createdAt: string;
  updatedAt: string;
  trackInventory?: boolean;
  managementCategory?: 'FIXED_ASSET' | 'LOW_VALUE_ASSET' | 'CONSUMABLE' | 'SALEABLE_STOCK' | 'SERVICE';
}

export interface ParsedMetadata {
  nameEn?: string;
  nameZh?: string;
  modelNumber?: string;
  brand?: string;
  productLine?: string;
  status?: string;
  isFeatured?: boolean;
  sortOrder?: number;
  listPrice?: number;
  costPrice?: number;
  discountRate?: number;
  currency?: string;
  minOrderQty?: number;
  leadTimeDays?: number;
  descriptionEn?: string;
  descriptionZh?: string;
  specsJson?: Record<string, string>;
  specsZhJson?: Record<string, string>;
  featuresJson?: { en: string; zh?: string }[];
  applications?: string;
  certifications?: string;
  videoUrl?: string;
  translationsJson?: Record<string, { name: string; description?: string; features?: string[] }>;
  seoTitle?: string;
  seoDescription?: string;
  adminNotes?: string;
}

export interface Category {
  id: string;
  nameEn: string;
  nameZh: string | null;
  slug: string;
  productLine: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
}

export const productApi = {
  /**
   * `GET /m1/products` filters on `search`, but every caller here passes the
   * app-wide `keyword`. The backend silently ignored it, so the product picker
   * in 报价单 returned an unfiltered list no matter what you typed. Translate
   * once here rather than at each call site.
   */
  list: (params: ListParams) => {
    const { keyword, ...rest } = params;
    return apiClient.get<PaginatedResponse<Product>>('/m1/products', {
      params: keyword ? { ...rest, search: keyword } : rest,
    });
  },

  getById: (id: string) =>
    apiClient.get<SingleResponse<Product>>(`/m1/products/${id}`),

  create: (data: Partial<Product>) =>
    apiClient.post<SingleResponse<Product>>('/m1/products', data),

  update: (id: string, data: Partial<Product>) =>
    apiClient.patch<SingleResponse<Product>>(`/m1/products/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m1/products/${id}`),

  listCategories: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<Category>>('/m1/categories', { params }),

  translateFields: (data: { nameEn?: string; nameZh?: string; descriptionEn?: string; descriptionZh?: string }) =>
    apiClient.post<{ data: { nameEn?: string; nameZh?: string; descriptionEn?: string; descriptionZh?: string } }>('/m1/products/translate-fields', data),

  translateAll: (productId: string, onlyMissing = false) =>
    apiClient.post<{ data: Record<string, { name: string; description?: string }> }>(`/m1/products/${productId}/translate-all`, { onlyMissing }),

  // Image gallery
  uploadImage: (productId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<{ data: { fileAssetId: string; imageUrl: string } }>(
      `/m1/products/${productId}/images`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  removeImage: (productId: string, fileAssetId: string) =>
    apiClient.delete(`/m1/products/${productId}/images/${fileAssetId}`),
  reorderImages: (productId: string, imageIds: string[]) =>
    apiClient.patch(`/m1/products/${productId}/images/reorder`, { imageIds }),

  // Metadata import/export
  parseMetadata: (text: string) =>
    apiClient.post<{ data: ParsedMetadata }>('/m1/products/parse-metadata', { text }),

  exportMetadata: (productId: string) =>
    apiClient.get<{ data: { text: string; filename: string } }>(`/m1/products/${productId}/export-metadata`),

  // Change history
  getHistory: (productId: string, page = 1, pageSize = 20) =>
    apiClient.get<PaginatedResponse<AuditLog>>(`/m1/products/${productId}/history`, { params: { page, pageSize } }),

  // Batch Excel import / export
  downloadTemplate: () =>
    apiClient.get('/m1/products/batch/template', { responseType: 'blob' }),

  batchImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<BatchImportResult>('/m1/products/batch/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  batchExport: (params?: { productLine?: string; status?: string; search?: string }) =>
    apiClient.get('/m1/products/batch/export', { params, responseType: 'blob' }),
};

export interface BatchImportResult {
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; field: string; message: string }>;
}

// ─── Audit Log ─────────────────────────────────────────────
export interface AuditLog {
  id: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string | null;
  changesJson: Record<string, { before: unknown; after: unknown }> | null;
  ipAddress: string | null;
  createdAt: string;
}

// ─── Product Documents ─────────────────────────────────────

export interface ProductDocument {
  id: string;
  productId: string;
  fileAssetId: string;
  docType: string;
  isPrimary: boolean;
  createdAt: string;
  fileAsset: {
    id: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
    storagePath: string;
  };
}

export const productDocumentApi = {
  list: (productId: string) =>
    apiClient.get<{ data: ProductDocument[] }>(`/m1/products/${productId}/documents`),

  upload: (productId: string, file: File, docType: string, isPrimary = false) => {
    const form = new FormData();
    form.append('file', file);
    form.append('docType', docType);
    form.append('isPrimary', String(isPrimary));
    return apiClient.post<{ data: ProductDocument }>(`/m1/products/${productId}/documents/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  delete: (productId: string, documentId: string) =>
    apiClient.delete(`/m1/products/${productId}/documents/${documentId}`),
};

// ─── M1 Product Relations ───────────────────────────────────
export interface ProductRelationTarget {
  id: string;
  nameEn: string;
  nameZh: string | null;
  modelNumber: string | null;
  productLine: string | null;
  imageUrls: string[] | null;
}

export interface ProductRelation {
  id: string;
  relationType: string;
  targetProductId: string;
  targetProduct: ProductRelationTarget;
  sortOrder: number;
}

export const productRelationApi = {
  getByProduct: (productId: string) =>
    apiClient.get<{ data: Record<string, ProductRelation[]> }>(`/m1/products/${productId}/relations`),

  add: (productId: string, data: { targetProductId: string; relationType: string; sortOrder?: number }) =>
    apiClient.post(`/m1/products/${productId}/relations`, data),

  remove: (productId: string, relationId: string) =>
    apiClient.delete(`/m1/products/${productId}/relations/${relationId}`),
};

// ─── M2 Supplier ───────────────────────────────────────────
export interface Supplier {
  id: string;
  companyNameEn: string;
  companyNameZh: string | null;
  contactEmail: string;
  contactPerson: string | null;
  contactPhone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  website: string | null;
  status: string;
  userId: string | null;
  rating: number | null;
  notesJson: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  // Finance / Compliance
  businessLicenseNo: string | null;
  taxId: string | null;
  bankName: string | null;
  bankAccountName: string | null;
  bankAccount: string | null;
  bankSwiftCode: string | null;
  bankRoutingCode: string | null;
}

export type SupplierRecognizedField =
  | 'companyNameEn'
  | 'companyNameZh'
  | 'contactEmail'
  | 'contactPerson'
  | 'contactPhone'
  | 'country'
  | 'city'
  | 'address'
  | 'website'
  | 'businessLicenseNo'
  | 'taxId'
  | 'bankName'
  | 'bankAccountName'
  | 'bankAccount'
  | 'bankSwiftCode'
  | 'bankRoutingCode';

export interface SupplierInputAnalysis {
  fields: Partial<Record<SupplierRecognizedField, string>>;
  recognizedFields: SupplierRecognizedField[];
  missingRequiredFields: Array<'companyNameEn' | 'contactEmail'>;
  warnings: string[];
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  source: 'RULES';
}

export interface CertExpiryItem {
  id: string;
  name_en: string;
  name_zh: string;
  cert_type: string;
  cert_number: string;
  expiry_date: string;
  days_left: number;
}

export interface Submission {
  id: string;
  supplierId: string;
  productNameEn: string;
  productNameZh: string | null;
  productLine: string;
  modelNumber: string | null;
  description: string | null;
  specsJson: Record<string, unknown> | null;
  status: string;
  reviewerNotes: string | null;
  productId: string | null;
  createdAt: string;
  updatedAt: string;
}

export const supplierApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<Supplier>>('/m2/suppliers', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<Supplier>>(`/m2/suppliers/${id}`),

  create: (data: Partial<Supplier>) =>
    apiClient.post<SingleResponse<Supplier>>('/m2/suppliers', data),

  update: (id: string, data: Partial<Supplier>) =>
    apiClient.patch<SingleResponse<Supplier>>(`/m2/suppliers/${id}`, data),

  analyzeInput: (text: string) =>
    apiClient.post<SingleResponse<SupplierInputAnalysis>>('/m2/suppliers/analyze-input', { text }),

  delete: (id: string) =>
    apiClient.delete(`/m2/suppliers/${id}`),

  approve: (id: string) =>
    apiClient.post(`/m2/suppliers/${id}/approve`),

  suspend: (id: string) =>
    apiClient.post(`/m2/suppliers/${id}/suspend`),

  contract: (id: string, format: 'docx' | 'pdf') =>
    apiClient.get(`/m2/suppliers/${id}/contract?format=${format}`, { responseType: 'blob' }),

  listSubmissions: (params: ListParams) =>
    apiClient.get<PaginatedResponse<Submission>>('/m2/submissions', { params }),

  getSubmission: (id: string) =>
    apiClient.get<SingleResponse<Submission>>(`/m2/submissions/${id}`),

  reviewSubmission: (id: string, action: 'approve' | 'reject', reviewerNotes?: string) =>
    apiClient.patch<SingleResponse<Submission>>(`/m2/submissions/${id}/review`, { action, reviewerNotes }),

  aiTranslate: (id: string) =>
    apiClient.post<SingleResponse<Submission>>(`/m2/submissions/${id}/ai-translate`),
};

// ─── M2 Supplier Update Requests ─────────────────────────────────────────

export interface SupplierUpdateRequest {
  id: string;
  supplierId: string;
  supplierName: string | null;
  productId: string | null;
  productCode: string | null;
  requestType: 'PRICE_UPDATE' | 'SPEC_UPDATE' | 'AVAILABILITY_UPDATE' | 'NEW_PRODUCT';
  updateData: Record<string, unknown>;
  notes: string | null;
  attachmentUrls: string[] | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'APPLIED';
  reviewNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export const supplierUpdateRequestApi = {
  list: (params?: { status?: string; page?: number; pageSize?: number }) =>
    apiClient.get<{ success: boolean; data: SupplierUpdateRequest[]; total: number; page: number; pageSize: number }>(
      '/m2/supplier-updates', { params }
    ),

  getById: (id: string) =>
    apiClient.get<{ success: boolean; data: SupplierUpdateRequest }>(`/m2/supplier-updates/${id}`),

  approve: (id: string, reviewNotes?: string) =>
    apiClient.patch<{ success: boolean; data: SupplierUpdateRequest }>(`/m2/supplier-updates/${id}/approve`, { reviewNotes }),

  reject: (id: string, reviewNotes: string) =>
    apiClient.patch<{ success: boolean; data: SupplierUpdateRequest }>(`/m2/supplier-updates/${id}/reject`, { reviewNotes }),

  markApplied: (id: string) =>
    apiClient.patch<{ success: boolean; data: SupplierUpdateRequest }>(`/m2/supplier-updates/${id}/applied`),
};

// ─── M2 Purchase Orders ──────────────────────────────────────────────────

export interface PurchaseOrderItem {
  id: string;
  orderId: string;
  productId: string | null;
  productName: string;
  brand?: string | null;
  modelNumber: string | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
  notes: string | null;
  sortOrder: number;
}

export interface PurchaseOrder {
  id: string;
  orderNo: string;
  supplierId: string;
  inquiryId: string | null;
  status: string;
  currency: string;
  totalAmount: number;
  paymentTerms: string | null;
  deliveryDate: string | null;
  shippingAddress: string | null;
  notes: string | null;
  createdBy: string | null;
  sentAt: string | null;
  confirmedAt: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  items: PurchaseOrderItem[];
  createdAt: string;
  updatedAt: string;
}

export const purchaseOrderApi = {
  list: (params: { page?: number; pageSize?: number; supplierId?: string; status?: string }) =>
    apiClient.get<PaginatedResponse<PurchaseOrder>>('/m2/purchase-orders', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<PurchaseOrder>>(`/m2/purchase-orders/${id}`),

  create: (data: {
    supplierId: string;
    inquiryId?: string;
    currency?: string;
    paymentTerms?: string;
    deliveryDate?: string;
    shippingAddress?: string;
    notes?: string;
    items: Array<{
      productId?: string;
      productName: string;
      modelNumber?: string;
      quantity: number;
      unit?: string;
      unitPrice: number;
      notes?: string;
      sortOrder?: number;
    }>;
  }) => apiClient.post<SingleResponse<PurchaseOrder>>('/m2/purchase-orders', data),

  update: (id: string, data: Partial<Pick<PurchaseOrder, 'currency' | 'paymentTerms' | 'deliveryDate' | 'shippingAddress' | 'notes'>>) =>
    apiClient.patch<SingleResponse<PurchaseOrder>>(`/m2/purchase-orders/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m2/purchase-orders/${id}`),

  send: (id: string) =>
    apiClient.post<SingleResponse<PurchaseOrder>>(`/m2/purchase-orders/${id}/send`),

  confirm: (id: string) =>
    apiClient.post<SingleResponse<PurchaseOrder>>(`/m2/purchase-orders/${id}/confirm`),

  ship: (id: string) =>
    apiClient.post<SingleResponse<PurchaseOrder>>(`/m2/purchase-orders/${id}/ship`),

  receive: (id: string) =>
    apiClient.post<SingleResponse<PurchaseOrder>>(`/m2/purchase-orders/${id}/receive`),

  cancel: (id: string) =>
    apiClient.post<SingleResponse<PurchaseOrder>>(`/m2/purchase-orders/${id}/cancel`),

  exportUrl: (id: string, format: 'html' | 'doc' = 'html') =>
    `/api/v1/m2/purchase-orders/${id}/export?format=${format}`,
};

// ─── M2 Supplier Evaluation ─────────────────────────────
export interface SupplierEvaluation {
  id: string;
  supplierId: string;
  evaluatorId: string;
  category: string;
  score: number;
  period: string;
  evaluationDate: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AggregateScore {
  category: string;
  avgScore: number;
}

export const supplierEvaluationApi = {
  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<SupplierEvaluation>>('/m2/supplier-evaluations', { params }),

  listBySupplier: (supplierId: string, params?: ListParams) =>
    apiClient.get<PaginatedResponse<SupplierEvaluation>>(`/m2/supplier-evaluations/by-supplier/${supplierId}`, { params }),

  getAggregateScores: (supplierId: string) =>
    apiClient.get<{ data: AggregateScore[] }>(`/m2/supplier-evaluations/by-supplier/${supplierId}/aggregate`),

  getById: (id: string) =>
    apiClient.get<SingleResponse<SupplierEvaluation>>(`/m2/supplier-evaluations/${id}`),

  create: (data: {
    supplierId: string;
    category: string;
    score: number;
    period: string;
    evaluationDate: string;
    notes?: string;
  }) => apiClient.post<SingleResponse<SupplierEvaluation>>('/m2/supplier-evaluations', data),

  update: (id: string, data: Partial<SupplierEvaluation>) =>
    apiClient.patch<SingleResponse<SupplierEvaluation>>(`/m2/supplier-evaluations/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m2/supplier-evaluations/${id}`),
};

// ─── M2 Supplier Communication ──────────────────────────
export interface SupplierCommunication {
  id: string;
  supplierId: string;
  userId: string;
  type: string;
  subject: string;
  content: string;
  contactPerson: string | null;
  communicatedAt: string;
  attachmentsJson: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export const supplierCommunicationApi = {
  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<SupplierCommunication>>('/m2/supplier-communications', { params }),

  listBySupplier: (supplierId: string, params?: ListParams) =>
    apiClient.get<PaginatedResponse<SupplierCommunication>>(`/m2/supplier-communications/by-supplier/${supplierId}`, { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<SupplierCommunication>>(`/m2/supplier-communications/${id}`),

  create: (data: {
    supplierId: string;
    type: string;
    subject: string;
    content: string;
    contactPerson?: string;
    communicatedAt: string;
    attachments?: string[];
  }) => apiClient.post<SingleResponse<SupplierCommunication>>('/m2/supplier-communications', data),

  update: (id: string, data: Partial<SupplierCommunication>) =>
    apiClient.patch<SingleResponse<SupplierCommunication>>(`/m2/supplier-communications/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m2/supplier-communications/${id}`),
};

// ─── M2 Supplier Stats ─────────────────────────────────
export interface SupplierStats {
  total: number;
  active: number;
  pending: number;
  suspended: number;
  blacklisted: number;
}

export const supplierStatsApi = {
  getStats: () =>
    apiClient.get<{ data: SupplierStats }>('/m2/suppliers/stats'),
};

// ─── M6 CRM ───────────────────────────────────────────────
export interface Customer {
  id: string;
  companyName: string | null;
  contactName: string;
  email: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  source: string;
  stage: string;
  ownerId: string | null;
  leadScore: number;
  tagsJson: string[] | null;
  notes: string | null;
  lastContactedAt: string | null;
  nextFollowUpAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Finance / Compliance
  businessLicenseNo: string | null;
  taxId: string | null;
  bankName: string | null;
  bankAccountName: string | null;
  bankAccount: string | null;
  bankSwiftCode: string | null;
}

export interface CustomerMapLocation {
  id: string;
  companyName: string | null;
  contactName: string;
  country: string | null;
  city: string | null;
  latitude: number;
  longitude: number;
  stage: string;
  ownerId: string | null;
}

export interface Inquiry {
  id: string;
  customerId: string | null;
  productId: string | null;
  source: string;
  contactName: string;
  contactEmail: string;
  companyName: string | null;
  message: string;
  status: string;
  priority: string;
  assignedTo: string | null;
  responseNotes: string | null;
  respondedAt: string | null;
  estimatedBudget: string | null;
  requiredDeliveryDate: string | null;
  aiSummary: string | null;
  detectedLanguage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PublicSeaCustomer {
  id: string;
  companyName: string | null;
  contactName: string;
  source: string;
  stage: string;
  lastContactedAt: string | null;
  releasedAt: string | null;
  seaType: string;
  ownerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerPoolRule {
  id: string;
  name: string;
  inactiveDays: number;
  applicableStages: string[];
  maxPerSales: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const customerApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<Customer>>('/m6/customers', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<Customer>>(`/m6/customers/${id}`),

  create: (data: Partial<Customer>) =>
    apiClient.post<SingleResponse<Customer>>('/m6/customers', data),

  update: (id: string, data: Partial<Customer>) =>
    apiClient.patch<SingleResponse<Customer>>(`/m6/customers/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m6/customers/${id}`),

  getMapLocations: () =>
    apiClient.get<{ data: CustomerMapLocation[] }>('/m6/customers/map-locations'),

  // Public sea (公海) APIs
  listPublicSea: (params: ListParams) =>
    apiClient.get<PaginatedResponse<PublicSeaCustomer>>('/m6/customers/public-sea', { params }),

  assign: (id: string, salesId: string) =>
    apiClient.post(`/m6/customers/${id}/assign`, { salesId }),

  bulkAssign: (customerIds: string[], salesId: string) =>
    apiClient.post('/m6/customers/bulk-assign', { customerIds, salesId }),

  release: (id: string, reason?: string) =>
    apiClient.post(`/m6/customers/${id}/release`, { reason }),

  bulkRelease: (customerIds: string[], reason?: string) =>
    apiClient.post('/m6/customers/bulk-release', { customerIds, reason }),

  claim: (id: string) =>
    apiClient.post(`/m6/customers/${id}/claim`),

  transfer: (id: string, newOwnerId: string, reason?: string) =>
    apiClient.post(`/m6/customers/${id}/transfer`, { newOwnerId, reason }),
};

export const customerPoolRuleApi = {
  list: () =>
    apiClient.get<PaginatedResponse<CustomerPoolRule>>('/m6/customer-pool-rules'),

  create: (data: Omit<CustomerPoolRule, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiClient.post<SingleResponse<CustomerPoolRule>>('/m6/customer-pool-rules', data),

  update: (id: string, data: Partial<Omit<CustomerPoolRule, 'id' | 'createdAt' | 'updatedAt'>>) =>
    apiClient.patch<SingleResponse<CustomerPoolRule>>(`/m6/customer-pool-rules/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m6/customer-pool-rules/${id}`),
};

// ─── M6 Follow-Up ─────────────────────────────────────────
export interface FollowUp {
  id: string;
  customerId: string;
  userId: string;
  type: string;
  content: string;
  contactedAt: string;
  nextFollowUpAt: string | null;
  result: string | null;
  createdAt: string;
  updatedAt: string;
}

export const followUpApi = {
  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<FollowUp>>('/m6/follow-ups', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<FollowUp>>(`/m6/follow-ups/${id}`),

  create: (data: {
    customerId: string;
    type: string;
    content: string;
    contactedAt: string;
    nextFollowUpAt?: string;
    result?: string;
  }) => apiClient.post<SingleResponse<FollowUp>>('/m6/follow-ups', data),

  update: (id: string, data: Partial<FollowUp>) =>
    apiClient.patch<SingleResponse<FollowUp>>(`/m6/follow-ups/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m6/follow-ups/${id}`),

  listByCustomer: (customerId: string, params?: ListParams) =>
    apiClient.get<PaginatedResponse<FollowUp>>(`/m6/follow-ups/by-customer/${customerId}`, { params }),
};

export interface VisitCheckin {
  id: string;
  customerId: string;
  userId: string;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  address: string | null;
  checkinAt: string;
  photoUrl: string | null;
  notes: string | null;
  distanceToCustomer: number | null;
  isValid: boolean;
  createdAt: string;
}

export const visitCheckinApi = {
  create: (data: {
    customerId: string;
    latitude: number;
    longitude: number;
    accuracy?: number;
    address?: string;
    checkinAt: string;
    photoUrl?: string;
    notes?: string;
  }) => apiClient.post<SingleResponse<VisitCheckin>>('/m6/visit-checkins', data),

  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<VisitCheckin>>('/m6/visit-checkins', { params }),

  listMy: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<VisitCheckin>>('/m6/visit-checkins/my', { params }),

  listByCustomer: (customerId: string, params?: ListParams) =>
    apiClient.get<PaginatedResponse<VisitCheckin>>(`/m6/customers/${customerId}/checkins`, { params }),
};

export interface CustomerBatchImportResult {
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{
    row: number;
    field: string;
    message: string;
    /** Excel column letter (e.g. "B") when the error is cell-specific. */
    column?: string;
    /** Template column header (e.g. "Email"). */
    header?: string;
    /** Offending value, truncated by the backend for safety. */
    value?: string;
    /** How the user should fix it. */
    suggestion?: string;
  }>;
}

export const customerBatchApi = {
  downloadTemplate: () =>
    apiClient.get('/m6/customers/batch/template', { responseType: 'blob' }),

  importFromExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<CustomerBatchImportResult>('/m6/customers/batch/import', formData);
  },

  exportToExcel: (params?: { source?: string; stage?: string; search?: string }) =>
    apiClient.get('/m6/customers/batch/export', { params, responseType: 'blob' }),
};

export const inquiryApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<Inquiry>>('/m6/inquiries', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<Inquiry>>(`/m6/inquiries/${id}`),

  create: (data: {
    contactName: string;
    contactEmail: string;
    message: string;
    companyName?: string;
    productId?: string;
    source?: string;
  }) =>
    apiClient.post<SingleResponse<Inquiry>>('/m6/inquiries', data),

  update: (id: string, data: Partial<Inquiry>) =>
    apiClient.patch<SingleResponse<Inquiry>>(`/m6/inquiries/${id}`, data),

  updateStatus: (id: string, status: string) =>
    apiClient.patch<SingleResponse<Inquiry>>(`/m6/inquiries/${id}/status`, { status }),

  assign: (id: string, userId: string) =>
    apiClient.patch<SingleResponse<Inquiry>>(`/m6/inquiries/${id}/assign`, { userId }),

  listByCustomer: (customerId: string) =>
    apiClient.get<{ data: Inquiry[] }>(`/m6/customers/${customerId}/inquiries`),
};

// ─── M4 Video ────────────────────────────────────────────
export interface VideoProject {
  id: string;
  titleEn: string;
  titleZh: string | null;
  productId: string | null;
  templateId: string | null;
  status: string;
  outputFileId: string | null;
  durationSeconds: number | null;
  resolution: string | null;
  configJson: Record<string, unknown> | null;
  createdBy: string;
  textTranslationsJson?: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

// ─── M4 Video Templates ─────────────────────────────────
export interface VideoTemplate {
  id: string;
  name: string;
  descriptionEn: string | null;
  descriptionZh: string | null;
  resolution: string;
  durationSeconds: number;
  thumbnailUrl: string | null;
  templateDataJson: Record<string, unknown> | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export const videoTemplateApi = {
  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<VideoTemplate>>('/m4/templates', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<VideoTemplate>>(`/m4/templates/${id}`),

  create: (data: Partial<VideoTemplate>) =>
    apiClient.post<SingleResponse<VideoTemplate>>('/m4/templates', data),

  update: (id: string, data: Partial<VideoTemplate>) =>
    apiClient.patch<SingleResponse<VideoTemplate>>(`/m4/templates/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m4/templates/${id}`),
};

export const videoApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<VideoProject>>('/m4/videos', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<VideoProject>>(`/m4/videos/${id}`),

  create: (data: Partial<VideoProject>) =>
    apiClient.post<SingleResponse<VideoProject>>('/m4/videos', data),

  update: (id: string, data: Partial<VideoProject>) =>
    apiClient.patch<SingleResponse<VideoProject>>(`/m4/videos/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m4/videos/${id}`),

  render: (id: string) =>
    apiClient.post(`/m4/videos/${id}/render`),

  translate: (id: string, sourceText: string, targetLanguages?: string[]) =>
    apiClient.post<SingleResponse<VideoProject>>(`/m4/videos/${id}/translate`, { sourceText, targetLanguages }),

  transcribe: (id: string, audioFilePath?: string) =>
    apiClient.post<{ jobId: string }>(`/m4/videos/${id}/transcribe`, { audioFilePath }),

  burnSubtitles: (id: string, languageCode: string) =>
    apiClient.post<{ jobId: string }>(`/m4/videos/${id}/burn-subtitles`, { languageCode }),
};

// ─── M4 Video Subtitles ──────────────────────────────────
export interface VideoSubtitle {
  id: string;
  videoProjectId: string;
  languageCode: string;
  format: string;
  content: string;
  isAutoGenerated: boolean;
  createdAt: string;
  updatedAt: string;
}

export const subtitleApi = {
  list: (videoId: string) =>
    apiClient.get<{ data: VideoSubtitle[] }>(`/m4/videos/${videoId}/subtitles`),

  getByLang: (videoId: string, lang: string) =>
    apiClient.get<SingleResponse<VideoSubtitle>>(`/m4/videos/${videoId}/subtitles/${lang}`),

  createOrUpdate: (videoId: string, data: { languageCode: string; format: string; content: string }) =>
    apiClient.post<SingleResponse<VideoSubtitle>>(`/m4/videos/${videoId}/subtitles`, data),

  translate: (videoId: string, sourceLang: string, targetLangs?: string[]) =>
    apiClient.post<{ data: VideoSubtitle[] }>(`/m4/videos/${videoId}/subtitles/translate`, { sourceLang, targetLangs }),

  delete: (videoId: string, lang: string) =>
    apiClient.delete(`/m4/videos/${videoId}/subtitles/${lang}`),
};

// ─── M4 Video Clips ──────────────────────────────────────
export interface VideoClip {
  id: string;
  videoProjectId: string;
  startTimeMs: number;
  endTimeMs: number;
  sortOrder: number;
  isSelected: boolean;
  thumbnailUrl?: string | null;
  notes?: string | null;
}

export const clipApi = {
  list: (videoId: string) =>
    apiClient.get<{ data: VideoClip[] }>(`/m4/videos/${videoId}/clips`),

  updateClips: (videoId: string, clips: Array<{ id: string; isSelected: boolean; sortOrder: number }>) =>
    apiClient.patch<{ data: VideoClip[] }>(`/m4/videos/${videoId}/clips`, { clips }),

  detectScenes: (videoId: string) =>
    apiClient.post<SingleResponse<{ message: string; jobId: string }>>(`/m4/videos/${videoId}/clips/detect-scenes`),

  renderEdit: (videoId: string) =>
    apiClient.post<SingleResponse<{ message: string; jobId: string }>>(`/m4/videos/${videoId}/clips/render-edit`),
};

// ─── M5 Social ───────────────────────────────────────────
export interface SocialAccount {
  id: string;
  platform: string;
  accountName: string;
  accountId: string | null;
  settingsJson?: { publishingMode?: 'MANUAL' | 'API'; profileUrl?: string; credentialExpiresAt?: string; authorType?: 'PERSON' | 'ORGANIZATION' } | null;
  hasCredentials: boolean;
  publishingMode: 'MANUAL' | 'API';
  connectionStatus: string;
  automaticPublishingSupported: boolean;
  isActive: boolean;
  followerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SocialPost {
  id: string;
  accountId: string;
  platform: string;
  title: string | null;
  content: string;
  mediaUrls: string[];
  language: string;
  approvalJson?: { approvedBy?: string; approvedAt?: string } | null;
  publicationJson?: { mode?: string; error?: string; url?: string; verification?: string; requestedBy?: string; requestedAt?: string; confirmedAt?: string } | null;
  videoProjectId: string | null;
  productId: string | null;
  status: string;
  scheduledAt: string | null;
  publishedAt: string | null;
  externalPostId: string | null;
  metricsJson: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export const socialApi = {
  draft: (data: { accountId: string; platform: string; content: string; language: string; title?: string }) =>
    apiClient.post<SingleResponse<{ content: string; reviewRequired: true; saved: false }>>('/m5/social/draft', data),
  summary: () => apiClient.get<SingleResponse<{ activeAccounts: number; totalPosts: number; published: number; publishedThisMonth: number; platformConfirmed: number; operatorReported: number }>>('/m5/social/summary'),
  // Accounts
  listAccounts: (params?: ListParams) =>
    apiClient.get<SingleResponse<SocialAccount[]>>('/m5/social/accounts', { params }),

  getAccountById: (id: string) =>
    apiClient.get<SingleResponse<SocialAccount>>(`/m5/social/accounts/${id}`),

  createAccount: (data: Partial<SocialAccount> & { accessToken?: string; publishingMode?: string; profileUrl?: string | null; credentialExpiresAt?: string | null; authorType?: string; clearCredentials?: boolean }) =>
    apiClient.post<SingleResponse<SocialAccount>>('/m5/social/accounts', data),

  updateAccount: (id: string, data: Partial<SocialAccount> & { accessToken?: string; publishingMode?: string; profileUrl?: string | null; credentialExpiresAt?: string | null; authorType?: string; clearCredentials?: boolean }) =>
    apiClient.patch<SingleResponse<SocialAccount>>(`/m5/social/accounts/${id}`, data),

  deleteAccount: (id: string) =>
    apiClient.delete(`/m5/social/accounts/${id}`),

  // Posts
  listPosts: (params: ListParams) =>
    apiClient.get<PaginatedResponse<SocialPost>>('/m5/social/posts', { params }),

  getPostById: (id: string) =>
    apiClient.get<SingleResponse<SocialPost>>(`/m5/social/posts/${id}`),

  createPost: (data: Partial<SocialPost>) =>
    apiClient.post<SingleResponse<SocialPost>>('/m5/social/posts', data),

  updatePost: (id: string, data: Partial<SocialPost>) =>
    apiClient.patch<SingleResponse<SocialPost>>(`/m5/social/posts/${id}`, data),

  deletePost: (id: string) =>
    apiClient.delete(`/m5/social/posts/${id}`),

  schedulePost: (id: string, scheduledAt: string) =>
    apiClient.post(`/m5/social/posts/${id}/schedule`, { scheduledAt }),

  publishPost: (id: string, retryConfirmed = false) =>
    apiClient.post(`/m5/social/posts/${id}/publish`, { retryConfirmed }),

  approvePost: (id: string, expectedUpdatedAt: string) => apiClient.post(`/m5/social/posts/${id}/approve`, { expectedUpdatedAt }),
  cancelSchedule: (id: string) => apiClient.post(`/m5/social/posts/${id}/cancel-schedule`),
  recordPublication: (id: string, data: { url: string; externalPostId?: string; note: string }) =>
    apiClient.post(`/m5/social/posts/${id}/receipt`, data),
};

// ─── M3 Website Content ───────────────────────────────────
export interface BlogPost {
  id: string;
  slug: string;
  titleEn: string;
  titleZh: string | null;
  excerptEn: string | null;
  excerptZh: string | null;
  contentEn: string;
  contentZh: string | null;
  category: string | null;
  coverImageUrl: string | null;
  author: string | null;
  readTime: number;
  status: string;
  publishedAt: string | null;
  isFeatured: boolean;
  sortOrder: number;
  seoTitle: string | null;
  seoDescription: string | null;
  viewCount: number;
  translationsJson?: Record<string, { title: string; excerpt?: string }> | null;
  createdAt: string;
  updatedAt: string;
}

export interface SolutionPage {
  id: string;
  slug: string;
  titleEn: string;
  titleZh: string | null;
  descriptionEn: string;
  descriptionZh: string | null;
  contentEn: string;
  contentZh: string | null;
  productLine: string;
  featuresEn: string[] | null;
  featuresZh: string[] | null;
  coverImageUrl: string | null;
  status: string;
  isFeatured: boolean;
  sortOrder: number;
  seoTitle: string | null;
  seoDescription: string | null;
  relatedProductIds: string[] | null;
  translationsJson?: Record<string, { title: string; description?: string }> | null;
  createdAt: string;
  updatedAt: string;
}

export const blogApi = {
  // Backend controller is /m3/blog-posts (not /m3/blog). Admin list uses admin/all.
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<BlogPost>>('/m3/blog-posts/admin/all', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<BlogPost>>(`/m3/blog-posts/${id}`),

  create: (data: Partial<BlogPost>) =>
    apiClient.post<SingleResponse<BlogPost>>('/m3/blog-posts', data),

  update: (id: string, data: Partial<BlogPost>) =>
    apiClient.patch<SingleResponse<BlogPost>>(`/m3/blog-posts/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m3/blog-posts/${id}`),

  publish: (id: string) =>
    apiClient.patch<SingleResponse<BlogPost>>(`/m3/blog-posts/${id}/publish`),

  translateAll: (id: string) =>
    apiClient.post<{ data: Record<string, { title: string; excerpt?: string }> }>(`/m3/blog-posts/${id}/translate-all`, {}),
};

export const solutionApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<SolutionPage>>('/m3/solutions', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<SolutionPage>>(`/m3/solutions/${id}`),

  create: (data: Partial<SolutionPage>) =>
    apiClient.post<SingleResponse<SolutionPage>>('/m3/solutions', data),

  update: (id: string, data: Partial<SolutionPage>) =>
    apiClient.patch<SingleResponse<SolutionPage>>(`/m3/solutions/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m3/solutions/${id}`),

  publish: (id: string) =>
    apiClient.patch<SingleResponse<SolutionPage>>(`/m3/solutions/${id}/publish`),

  translateAll: (id: string) =>
    apiClient.post<{ data: Record<string, { title: string; description?: string }> }>(`/m3/solutions/${id}/translate-all`, {}),
};

// ─── Dashboard Stats ───────────────────────────────────────
export interface DashboardStats {
  // Core counts
  totalProducts: number;
  totalSuppliers: number;
  totalCustomers: number;
  totalInquiries: number;
  pendingInquiries: number;
  videoProjects: number;
  socialAccounts: number;
  inquiriesByStatus: Record<string, number>;
  productsByLine: Record<string, number>;
  recentInquiries: Inquiry[];
  // Business KPIs
  totalOrders: number;
  ordersThisMonth: number;
  revenueTotal: number;
  revenueThisMonth: number;
  revenueLastMonth: number;
  ordersByStatus: Record<string, number>;
  arOutstanding: number;
  arOverdue: number;
  onlinePaymentsThisMonth: number;
  onlinePaymentVolumeThisMonth: number;
  recentOrders: Array<{
    id: string;
    orderNo: string;
    status: string;
    paymentStatus: string;
    totalAmount: number;
    currency: string;
    guestName?: string;
    guestCompany?: string;
    customerId?: string;
    createdAt: string;
  }>;
  monthlyRevenue: Array<{ month: string; revenue: number; orders: number }>;
  // CRM Pipeline KPIs
  totalLeads: number;
  newLeadsThisMonth: number;
  leadsByStatus: Record<string, number>;
  openOpportunities: number;
  pipelineValue: number;
  wonOpportunitiesThisMonth: number;
  wonValueThisMonth: number;
  winRate: number;
  leadConversionRate: number;
  overdueTasks: number;
  pipelineByStage: Array<{ stage: string; count: number; amount: number }>;
}

export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const res = await apiClient.get<SingleResponse<DashboardStats>>('/dashboard/stats');
    return res.data.data;
  },

  getRecentInquiries: () =>
    apiClient.get<PaginatedResponse<Inquiry>>('/m6/inquiries', {
      params: { page: 1, pageSize: 5, sort: '-createdAt' },
    }),

  getRecentSubmissions: () =>
    apiClient.get<PaginatedResponse<Submission>>('/m2/submissions', {
      params: { page: 1, pageSize: 5, sort: '-createdAt' },
    }),

  getCertExpirySummary: async (): Promise<{
    total: number;
    critical: CertExpiryItem[];
    warning: CertExpiryItem[];
    notice: CertExpiryItem[];
  }> => {
    const res = await apiClient.get<{ data: {
      total: number;
      critical: CertExpiryItem[];
      warning: CertExpiryItem[];
      notice: CertExpiryItem[];
    } }>('/m1/products/admin/cert-expiry-summary');
    return res.data.data;
  },
};

// ─── Email Templates ─────────────────────────────────────
export interface EmailTemplate {
  id: string;
  name: string;
  type: string;
  subjectEn: string;
  subjectZh: string | null;
  bodyHtmlEn: string;
  bodyHtmlZh: string | null;
  bodyTextEn: string | null;
  bodyTextZh: string | null;
  isActive: boolean;
  variablesJson: Record<string, string> | null;
  translationsJson?: Record<string, { subject: string; bodyHtml: string; bodyText?: string }> | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailLog {
  id: string;
  templateId: string | null;
  toEmail: string;
  toName: string | null;
  subject: string;
  status: string;
  messageId: string | null;
  errorMessage: string | null;
  sentAt: string | null;
  customerId: string | null;
  inquiryId: string | null;
  createdAt: string;
}

export const emailTemplateApi = {
  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<EmailTemplate>>('/m6/email-templates', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<EmailTemplate>>(`/m6/email-templates/${id}`),

  create: (data: Partial<EmailTemplate>) =>
    apiClient.post<SingleResponse<EmailTemplate>>('/m6/email-templates', data),

  update: (id: string, data: Partial<EmailTemplate>) =>
    apiClient.patch<SingleResponse<EmailTemplate>>(`/m6/email-templates/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m6/email-templates/${id}`),

  aiTranslate: (data: {
    subjectEn: string;
    bodyHtmlEn: string;
    bodyTextEn?: string;
    languages: string[];
  }) =>
    apiClient.post<{ data: Record<string, { subject: string; bodyHtml: string; bodyText?: string }> }>(
      '/m6/email-templates/translate',
      data,
    ),
};

/** 序列邮件人工审核队列 */
export interface PendingApprovalEmail extends EmailLog {
  bodyHtml: string | null;
  enrollmentId: string | null;
  sequenceStepOrder: number | null;
}

export const emailApprovalApi = {
  listPending: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<PendingApprovalEmail>>('/m6/email-sequences/approvals/pending', { params }),

  config: () =>
    apiClient.get<SingleResponse<{ approvalRequired: boolean }>>('/m6/email-sequences/approvals/config'),

  approve: (emailLogId: string) =>
    apiClient.post<SingleResponse<EmailLog>>(`/m6/email-sequences/approvals/${emailLogId}/approve`),

  reject: (emailLogId: string, reason: string) =>
    apiClient.post(`/m6/email-sequences/approvals/${emailLogId}/reject`, { reason }),
};

export const emailLogApi = {
  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<EmailLog>>('/m6/email-logs', { params }),

  listByCustomer: (customerId: string, params?: ListParams) =>
    apiClient.get<PaginatedResponse<EmailLog>>('/m6/email-logs', { params: { ...params, customerId } }),
};

export const emailApi = {
  sendMarketing: (data: {
    templateId: string;
    customerId: string;
    toEmail: string;
    toName?: string;
    productName?: string;
    extraVars?: Record<string, string>;
  }) => apiClient.post<SingleResponse<EmailLog>>('/m6/email-templates/send-marketing', data),
};

// ─── Inquiry Notes ────────────────────────────────────────
export interface InquiryNote {
  id: string;
  inquiryId: string;
  userId: string | null;
  userDisplayName: string | null;
  content: string;
  type: string;
  metaJson: Record<string, unknown> | null;
  createdAt: string;
}

export const inquiryNoteApi = {
  list: (inquiryId: string) =>
    apiClient.get<{ data: InquiryNote[] }>(`/m6/inquiries/${inquiryId}/notes`),
  create: (inquiryId: string, data: { content: string; type?: string }) =>
    apiClient.post<SingleResponse<InquiryNote>>(`/m6/inquiries/${inquiryId}/notes`, data),
};

// ─── M6 Quotes ────────────────────────────────────────────
export interface QuoteLineItem {
  id?: string;
  productId?: string;
  productName: string;
  modelNumber?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  amount: number;
  notes?: string;
  sortOrder?: number;
}

export interface Quote {
  id: string;
  quoteNo: string;
  customerId: string;
  customer?: Customer;
  inquiryId?: string;
  status: string;
  currency: string;
  subtotal: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  validUntil?: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  /** 'domestic' 内贸 | 'export' 外贸 — carried through to the contract on conversion */
  tradeType?: string;
  incoterms?: string | null;
  language?: string;
  notes?: string;
  items: QuoteLineItem[];
  createdAt: string;
  updatedAt: string;
}

export const quoteApi = {
  list: (params?: { page?: number; pageSize?: number; customerId?: string; opportunityId?: string; status?: string }) =>
    apiClient.get<PaginatedResponse<Quote>>('/quotes', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<Quote>>(`/quotes/${id}`),

  create: (data: Partial<Quote>) =>
    apiClient.post<SingleResponse<Quote>>('/quotes', data),

  update: (id: string, data: Partial<Quote>) =>
    apiClient.patch<SingleResponse<Quote>>(`/quotes/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/quotes/${id}`),

  approve: (id: string) =>
    apiClient.post(`/quotes/${id}/approve`),

  reject: (id: string, reason?: string) =>
    apiClient.post(`/quotes/${id}/reject`, { reason }),
};

// ─── M6 Lead ──────────────────────────────────────────────
export interface Lead {
  id: string;
  source: string;
  status: string;
  contactName: string;
  contactEmail?: string;
  contactPhone?: string;
  companyName?: string;
  notes?: string;
  assignedTo?: string;
  convertedContactId?: string;
  createdAt: string;
  updatedAt: string;
}

export const leadApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string; assignedTo?: string }) =>
    apiClient.get('/m6/leads', { params }),
  getById: (id: string) => apiClient.get(`/m6/leads/${id}`),
  create: (data: Partial<Lead>) => apiClient.post('/m6/leads', data),
  update: (id: string, data: Partial<Lead>) => apiClient.patch(`/m6/leads/${id}`, data),
  claim: (id: string) => apiClient.patch(`/m6/leads/${id}/claim`),
  convert: (id: string, data: { contactId?: string; companyId?: string }) =>
    apiClient.patch(`/m6/leads/${id}/convert`, data),
  bulkUpdate: (data: { ids: string[]; assignedTo?: string; status?: string }) =>
    apiClient.patch('/m6/leads/bulk', data),
};

// ─── M6 Opportunity ───────────────────────────────────────
export interface Opportunity {
  id: string;
  title: string;
  stage: string;
  amount?: number;
  currency?: string;
  probability?: number;
  expectedCloseDate?: string;
  contactId?: string;
  companyId?: string;
  assignedTo?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export const opportunityApi = {
  list: (params?: { page?: number; pageSize?: number; stage?: string; assignedTo?: string }) =>
    apiClient.get('/m6/opportunities', { params }),
  getById: (id: string) => apiClient.get(`/m6/opportunities/${id}`),
  create: (data: Partial<Opportunity>) => apiClient.post('/m6/opportunities', data),
  update: (id: string, data: Partial<Opportunity>) => apiClient.patch(`/m6/opportunities/${id}`, data),
  advanceStage: (id: string) => apiClient.patch(`/m6/opportunities/${id}/advance`),
  markWon: (id: string) => apiClient.patch(`/m6/opportunities/${id}/won`),
  markLost: (id: string, data: { reason?: string }) => apiClient.patch(`/m6/opportunities/${id}/lost`, data),
  funnelSummary: () => apiClient.get('/m6/opportunities/funnel'),
};

// ─── M6 Contract ──────────────────────────────────────────
// Field names mirror ContractEntity / CreateContractDto exactly. They used to
// be value/startDate/endDate/contactId/companyId, none of which the API ever
// returned — so the list showed "—" for amount and dates, and the edit form
// posted a body the backend ignored.
export interface Contract {
  id: string;
  contractNo?: string | null;
  qrToken: string;
  title: string;
  status: string;
  customerId: string;
  amount?: number;
  currency?: string;
  signedDate?: string;
  effectiveDate?: string;
  expireDate?: string;
  /** 'domestic' 内贸 | 'export' 外贸 — drives currency + document template */
  tradeType?: string;
  /** Incoterms 2020 (FOB / CIF / EXW / DDP …) — export only */
  incoterms?: string | null;
  /** 'zh' | 'en' | 'bilingual' */
  language?: string;
  opportunityId?: string;
  quoteId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export const contractApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string; customerId?: string }) =>
    apiClient.get<PaginatedResponse<Contract>>('/m6/contracts', { params }),
  getById: (id: string) => apiClient.get<SingleResponse<Contract>>(`/m6/contracts/${id}`),
  create: (data: Partial<Contract>) => apiClient.post<SingleResponse<Contract>>('/m6/contracts', data),
  update: (id: string, data: Partial<Contract>) => apiClient.patch<SingleResponse<Contract>>(`/m6/contracts/${id}`, data),
  delete: (id: string) => apiClient.delete(`/m6/contracts/${id}`),
  getPayments: (id: string) => apiClient.get(`/m6/contracts/${id}/payments`),
  /**
   * Downloads the signed-paper version as a .doc.
   *
   * Goes through apiClient rather than `window.open` so the Bearer token is
   * attached — the export endpoint is behind the same auth guard as the rest,
   * and a plain window.open would just 401.
   *
   * `template`: 'domestic' = 国内购销合同版式, 'trade' = 外贸双语版式,
   * 'auto' = follow the contract's tradeType.
   */
  download: async (
    id: string,
    contractNo?: string | null,
    template: 'auto' | 'domestic' | 'trade' = 'auto',
  ): Promise<void> => {
    const res = await apiClient.get(`/m6/contracts/${id}/export`, {
      params: { format: 'doc', template },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${contractNo ?? id}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ─── M6 Trade flow (报价单 → 合同 → 销售订单) ───────────────
export const tradeFlowApi = {
  /** Converts a quote into a contract, its payment plan and its receivable, in one transaction. */
  convertQuoteToContract: (
    quoteId: string,
    data?: {
      title?: string;
      signedDate?: string;
      effectiveDate?: string;
      expireDate?: string;
      tradeType?: 'domestic' | 'export';
      incoterms?: string;
      language?: 'zh' | 'en' | 'bilingual';
      notes?: string;
    },
  ) => apiClient.post(`/m6/trade-flow/quotes/${quoteId}/convert-to-contract`, data ?? {}),

  spawnSalesOrder: (contractId: string, data?: Record<string, unknown>) =>
    apiClient.post(`/m6/trade-flow/contracts/${contractId}/spawn-sales-order`, data ?? {}),
};

// ─── M6 Invoice ───────────────────────────────────────────
export interface Invoice {
  id: string;
  invoiceNo: string;
  status: string;
  amount: number;
  currency?: string;
  dueDate?: string;
  issuedDate?: string;
  contractId?: string;
  contactId?: string;
  companyId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export const invoiceApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string; customerId?: string }) =>
    apiClient.get<PaginatedResponse<Invoice>>('/m6/invoices', { params }),
  getById: (id: string) => apiClient.get<SingleResponse<Invoice>>(`/m6/invoices/${id}`),
  create: (data: Partial<Invoice>) => apiClient.post<SingleResponse<Invoice>>('/m6/invoices', data),
  markPaid: (id: string) => apiClient.patch(`/m6/invoices/${id}/mark-paid`),
  markSent: (id: string) => apiClient.patch(`/m6/invoices/${id}/mark-sent`),
};

// ─── M6 Payment ───────────────────────────────────────────
export interface Payment {
  id: string;
  amount: number;
  currency?: string;
  paidAt?: string;
  method?: string;
  contractId?: string;
  invoiceId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export const paymentApi = {
  list: (params?: { page?: number; pageSize?: number; contractId?: string }) =>
    apiClient.get('/m6/payments', { params }),
  getById: (id: string) => apiClient.get(`/m6/payments/${id}`),
  create: (data: Partial<Payment>) => apiClient.post('/m6/payments', data),
  record: (id: string, data: Partial<Payment>) => apiClient.patch(`/m6/payments/${id}/record`, data),
  overdue: () => apiClient.get('/m6/payments/overdue'),
};

// ─── M6 Task ──────────────────────────────────────────────
export interface CrmTask {
  id: string;
  title: string;
  status: string;
  priority?: string;
  dueDate?: string;
  contactId?: string;
  companyId?: string;
  assignedTo?: string;
  notes?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const taskApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string; assignedTo?: string }) =>
    apiClient.get('/m6/tasks', { params }),
  getById: (id: string) => apiClient.get(`/m6/tasks/${id}`),
  create: (data: Partial<CrmTask>) => apiClient.post('/m6/tasks', data),
  update: (id: string, data: Partial<CrmTask>) => apiClient.patch(`/m6/tasks/${id}`, data),
  complete: (id: string) => apiClient.patch(`/m6/tasks/${id}/complete`),
  delete: (id: string) => apiClient.delete(`/m6/tasks/${id}`),
  overdue: () => apiClient.get('/m6/tasks/overdue'),
};

// ─── M6 Contact Record ────────────────────────────────────
export interface ContactRecord {
  id: string;
  type: string;
  summary: string;
  contactId?: string;
  companyId?: string;
  recordedBy?: string;
  recordedAt?: string;
  isConfirmed?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const contactRecordApi = {
  list: (params?: { page?: number; pageSize?: number; contactId?: string; companyId?: string }) =>
    apiClient.get('/m6/contact-records', { params }),
  getById: (id: string) => apiClient.get(`/m6/contact-records/${id}`),
  create: (data: Partial<ContactRecord>) => apiClient.post('/m6/contact-records', data),
  update: (id: string, data: Partial<ContactRecord>) => apiClient.patch(`/m6/contact-records/${id}`, data),
  delete: (id: string) => apiClient.delete(`/m6/contact-records/${id}`),
  confirm: (id: string) => apiClient.patch(`/m6/contact-records/${id}/confirm`),
};

// ─── M6 BI Analytics ──────────────────────────────────────
export const biAnalyticsApi = {
  revenue: (params?: { startDate?: string; endDate?: string }) =>
    apiClient.get('/m6/bi/revenue', { params }),
  pipeline: () => apiClient.get('/m6/bi/pipeline'),
  funnel: () => apiClient.get('/m6/bi/funnel'),
  teamPerformance: (params?: { startDate?: string; endDate?: string }) =>
    apiClient.get('/m6/bi/team-performance', { params }),
  aiTrend: () => apiClient.get('/m6/bi/ai-trend'),
};

// ─── M6 CRM Product ───────────────────────────────────────
export interface CrmProduct {
  id: string;
  name: string;
  sku?: string;
  category?: string;
  unitPrice: number;
  currency?: string;
  unit?: string;
  taxRate?: number;
  discountPolicy?: Record<string, unknown>;
  description?: string;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const crmProductApi = {
  list: (params?: { page?: number; pageSize?: number; search?: string; category?: string }) =>
    apiClient.get('/m6/crm-products', { params }),
  getById: (id: string) => apiClient.get(`/m6/crm-products/${id}`),
  create: (data: Partial<CrmProduct>) => apiClient.post('/m6/crm-products', data),
  update: (id: string, data: Partial<CrmProduct>) => apiClient.patch(`/m6/crm-products/${id}`, data),
  delete: (id: string) => apiClient.delete(`/m6/crm-products/${id}`),
};

// ─── Admin User Management ────────────────────────────────

export interface AdminCreateUserPayload {
  email: string;
  password: string;
  displayName: string;
  role: string;
  phone?: string;
  inventoryAccessScope?: 'GLOBAL' | 'WAREHOUSES';
  inventoryWarehouseIds?: string[];
}

export interface AdminUpdateUserPayload {
  displayName?: string;
  role?: string;
  phone?: string;
  isActive?: boolean;
  password?: string;
  inventoryAccessScope?: 'GLOBAL' | 'WAREHOUSES';
  inventoryWarehouseIds?: string[];
}

export const userApi = {
  list: (params?: ListParams) =>
    apiClient.get<PaginatedResponse<UserProfile>>('/auth/users', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<UserProfile>>(`/auth/users/${id}`),

  create: (data: AdminCreateUserPayload) =>
    apiClient.post<SingleResponse<UserProfile>>('/auth/users', data),

  update: (id: string, data: AdminUpdateUserPayload) =>
    apiClient.patch<SingleResponse<UserProfile>>(`/auth/users/${id}`, data),
};

// ============================================================
// Email Client APIs
// ============================================================

export interface EmailAccount {
  id: string;
  name: string;
  email: string;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  imapUser: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  isActive: boolean;
  lastSyncAt: string | null;
  syncStatus: 'IDLE' | 'SYNCING' | 'ERROR';
  syncErrorMsg: string | null;
  syncMaxCount: number;
  createdAt: string;
}

export interface EmailMessage {
  id: string;
  accountId: string;
  uid: number | null;
  messageId: string | null;
  inReplyTo: string | null;
  threadId: string | null;
  direction: 'INBOUND' | 'OUTBOUND';
  folder: string;
  fromEmail: string;
  fromName: string | null;
  toEmailsJson: string[];
  ccEmailsJson: string[] | null;
  subject: string;
  bodyText: string | null;
  bodyHtml: string | null;
  attachmentsJson: Array<{ filename: string; size: number; contentType: string }> | null;
  sentAt: string;
  isRead: boolean;
  isStarred: boolean;
  customerId: string | null;
  inquiryId: string | null;
  aiSummary: string | null;
  aiDraft?: string | null;
  createdAt: string;
}

interface EmailAccountCreateData {
  name: string;
  email: string;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  imapUser: string;
  imapPassword: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
  syncMaxCount?: number;
}

export const emailAccountApi = {
  list: () =>
    apiClient.get<{ data: EmailAccount[] }>('/m6/email-accounts'),
  create: (data: EmailAccountCreateData) =>
    apiClient.post<{ data: EmailAccount }>('/m6/email-accounts', data),
  update: (id: string, data: Partial<EmailAccountCreateData>) =>
    apiClient.patch<{ data: EmailAccount }>(`/m6/email-accounts/${id}`, data),
  delete: (id: string) =>
    apiClient.delete(`/m6/email-accounts/${id}`),
  test: (id: string) =>
    apiClient.post<{ data: { success: boolean; error?: string } }>(`/m6/email-accounts/${id}/test`),
  sync: (id: string) =>
    apiClient.post<{ data: { synced: number } }>(`/m6/email-accounts/${id}/sync`),
};

export const emailInboxApi = {
  list: (params: {
    accountId?: string;
    folder?: string;
    customerId?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    isRead?: boolean;
    isStarred?: boolean;
  }) => apiClient.get<PaginatedResponse<EmailMessage>>('/m6/email-messages', { params }),
  getById: (id: string) =>
    apiClient.get<{ data: EmailMessage }>(`/m6/email-messages/${id}`),
  send: (data: {
    accountId: string;
    to: string[];
    cc?: string[];
    subject: string;
    bodyHtml: string;
    inReplyTo?: string;
    customerId?: string;
    inquiryId?: string;
  }) => apiClient.post<{ data: EmailMessage }>('/m6/email-messages/send', data),
  markRead: (id: string, isRead: boolean) =>
    apiClient.patch(`/m6/email-messages/${id}/read`, { isRead }),
  toggleStar: (id: string) =>
    apiClient.patch<{ data: { isStarred: boolean } }>(`/m6/email-messages/${id}/star`),
  linkCustomer: (id: string, customerId: string | null) =>
    apiClient.patch(`/m6/email-messages/${id}/link`, { customerId }),
  delete: (id: string) =>
    apiClient.delete(`/m6/email-messages/${id}`),
};

export const emailAiApi = {
  customerResearch: (customerId: string, context?: string) =>
    apiClient.post<{ data: { result: string } }>('/m6/email-ai/customer-research', { customerId, context }),
  communicationSummary: (customerId: string) =>
    apiClient.post<{ data: { summary: string } }>('/m6/email-ai/communication-summary', { customerId }),
  nextSteps: (customerId: string) =>
    apiClient.post<{ data: { suggestions: string[] } }>('/m6/email-ai/next-steps', { customerId }),
  draftReply: (messageId: string, instructions?: string, language?: 'auto' | 'vi+en' | 'vi' | 'en' | 'zh') =>
    apiClient.post<{ data: { draft: string } }>('/m6/email-ai/draft-reply', { messageId, instructions, language }),
};

// ─── Batch Import/Export Shared Type ──────────────────────
export interface BatchImportResult {
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; field: string; message: string }>;
}

// ─── M2 Supplier Batch ────────────────────────────────────
export const supplierBatchApi = {
  downloadTemplate: () =>
    apiClient.get('/m2/suppliers/batch/template', { responseType: 'blob' }),

  importFromExcel: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<BatchImportResult>('/m2/suppliers/batch/import', form);
  },

  exportToExcel: (params?: { status?: string; search?: string }) =>
    apiClient.get('/m2/suppliers/batch/export', { params, responseType: 'blob' }),
};

// ─── M1 Product Batch ─────────────────────────────────────

/**
 * Per-file row returned by the markdown batch importer.  Mirrors the
 * shape defined in apps/api/src/modules/m1-product/services/markdown-batch-import.service.ts.
 */
export interface MarkdownFileResult {
  filename: string;
  action: 'created' | 'updated' | 'skipped';
  slug: string;
  productId?: string;
  error?: string;
  warnings?: string[];
}

export interface MarkdownBatchImportResult {
  totalFiles: number;
  created: number;
  updated: number;
  skipped: number;
  files: MarkdownFileResult[];
  errors: Array<{ row: number; field: string; message: string }>;
}

export const productBatchApi = {
  downloadTemplate: () =>
    apiClient.get('/m1/products/batch/template', { responseType: 'blob' }),

  importFromExcel: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<BatchImportResult>('/m1/products/batch/import', form);
  },

  /**
   * Batch-import N markdown metadata files (.md / .markdown / .txt).
   * Backed by POST /api/v1/m1/products/batch/import-markdown.  The parser
   * tolerates non-standard bullet markers (`*`, `•`, numbered lists, etc.)
   * and full-width colons inside key:value pairs.
   */
  importFromMarkdown: (files: File[]) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    return apiClient.post<MarkdownBatchImportResult>(
      '/m1/products/batch/import-markdown',
      form,
    );
  },

  exportToExcel: (params?: { productLine?: string; status?: string; search?: string }) =>
    apiClient.get('/m1/products/batch/export', { params, responseType: 'blob' }),
};

// ─── M6 Company ───────────────────────────────────────────
export interface Company {
  id: string;
  name: string;
  industry: string | null;
  size: string | null;
  city: string | null;
  country: string | null;
  website: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const companyApi = {
  list: (params?: { page?: number; pageSize?: number; search?: string; city?: string; industry?: string; size?: string }) =>
    apiClient.get<PaginatedResponse<Company>>('/m6/companies', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<Company>>(`/m6/companies/${id}`),

  getContacts: (id: string, includePast?: boolean) =>
    apiClient.get<{ data: unknown[] }>(`/m6/companies/${id}/contacts`, { params: { includePast } }),

  create: (data: Partial<Company>) =>
    apiClient.post<SingleResponse<Company>>('/m6/companies', data),

  update: (id: string, data: Partial<Company>) =>
    apiClient.patch<SingleResponse<Company>>(`/m6/companies/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m6/companies/${id}`),
};

// ─── M6 Two-Factor Auth ───────────────────────────────────
export interface TwoFactorStatus {
  isEnabled: boolean;
  method: string;
  backupCodesRemaining: number;
}

export interface TwoFactorSetupResult {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

export const twoFactorAuthApi = {
  getStatus: () =>
    apiClient.get<SingleResponse<TwoFactorStatus>>('/m6/auth/2fa/status'),

  generate: () =>
    apiClient.post<SingleResponse<TwoFactorSetupResult>>('/m6/auth/2fa/generate'),

  enable: (token: string) =>
    apiClient.post<{ success: boolean }>('/m6/auth/2fa/enable', { token }),

  verify: (token: string) =>
    apiClient.post<{ success: boolean }>('/m6/auth/2fa/verify', { token }),

  disable: () =>
    apiClient.delete<{ message: string }>('/m6/auth/2fa/disable'),
};

// ─── M6 IP Whitelist ──────────────────────────────────────
export interface IpWhitelistEntry {
  id: string;
  ip: string;
  cidr: string | null;
  description: string | null;
  action: string;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const ipWhitelistApi = {
  list: () =>
    apiClient.get<IpWhitelistEntry[]>('/m6/ip-whitelist'),

  create: (data: { ip: string; description?: string; action: string; cidr?: string }) =>
    apiClient.post<IpWhitelistEntry>('/m6/ip-whitelist', data),

  update: (id: string, data: { ip?: string; description?: string; action?: string; cidr?: string }) =>
    apiClient.patch<IpWhitelistEntry>(`/m6/ip-whitelist/${id}`, data),

  toggle: (id: string) =>
    apiClient.post<IpWhitelistEntry>(`/m6/ip-whitelist/${id}/toggle`),

  delete: (id: string) =>
    apiClient.delete(`/m6/ip-whitelist/${id}`),

  checkIp: (ip: string) =>
    apiClient.get<{ action: string }>('/m6/ip-whitelist/check', { params: { ip } }),
};

// ─── M6 AI Quality ────────────────────────────────────────
export interface AiUsageSummary {
  totalCalls: number;
  totalTokensUsed: number;
  totalCostUsd: number | null;
  successRate: number;
  avgDurationMs: number;
  byModule: Record<string, number>;
  byModel: Record<string, number>;
}

export interface AiUsageLog {
  id: string;
  tenantId: string | null;
  userId: string | null;
  module: string;
  actionType: string;
  model: string | null;
  tokensUsed: number | null;
  durationMs: number | null;
  status: string;
  feedback: string | null;
  createdAt: string;
}

export const aiQualityApi = {
  getUsage: (params?: { tenantId?: string; userId?: string; days?: number }) =>
    apiClient.get<AiUsageSummary>('/m6/ai-quality/usage', { params }),

  getLogs: (params?: { page?: number; pageSize?: number; tenantId?: string; userId?: string; actionType?: string }) =>
    apiClient.get<PaginatedResponse<AiUsageLog>>('/m6/ai-quality/logs', { params }),

  recordFeedback: (id: string, feedback: string) =>
    apiClient.post<{ success: boolean }>(`/m6/ai-quality/logs/${id}/feedback`, { feedback }),

  checkQuota: (params?: { tenantId?: string; userId?: string; isPro?: boolean }) =>
    apiClient.get<{ allowed: boolean; remaining: number; message?: string }>('/m6/ai-quality/quota', { params }),
};

// ─── M6 PIPL Compliance ───────────────────────────────────
export interface PiplRequest {
  id: string;
  tenantId: string;
  subjectName: string;
  subjectEmail: string;
  subjectId: string | null;
  requestType: string;
  description: string | null;
  status: string;
  handlerId: string | null;
  notes: Array<{ note: string; addedBy: string; addedAt: string }> | null;
  dueAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DataConsent {
  id: string;
  tenantId: string;
  subjectId: string;
  subjectType: string;
  consentType: string;
  isGranted: boolean;
  policyVersion: string | null;
  consentChannel: string | null;
  ipAddress: string | null;
  grantedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export const piplApi = {
  submitRequest: (data: { subjectName: string; subjectEmail: string; requestType: string; description?: string; subjectId?: string }) =>
    apiClient.post<SingleResponse<PiplRequest>>('/m6/pipl/requests', data),

  listRequests: (params?: { status?: string; page?: number; limit?: number }) =>
    apiClient.get<PaginatedResponse<PiplRequest>>('/m6/pipl/requests', { params }),

  assignHandler: (id: string, handlerId: string) =>
    apiClient.patch<SingleResponse<PiplRequest>>(`/m6/pipl/requests/${id}/assign`, { handlerId }),

  addNote: (id: string, note: string) =>
    apiClient.post<SingleResponse<PiplRequest>>(`/m6/pipl/requests/${id}/note`, { note }),

  complete: (id: string) =>
    apiClient.post<SingleResponse<PiplRequest>>(`/m6/pipl/requests/${id}/complete`),

  reject: (id: string, reason: string) =>
    apiClient.post<SingleResponse<PiplRequest>>(`/m6/pipl/requests/${id}/reject`, { reason }),

  getSubjectConsents: (subjectId: string) =>
    apiClient.get<{ data: DataConsent[] }>(`/m6/pipl/consents/${subjectId}`),

  grantConsent: (data: { subjectId: string; subjectType: string; consentType: string; policyVersion?: string; consentChannel?: string; ipAddress?: string }) =>
    apiClient.post<SingleResponse<DataConsent>>('/m6/pipl/consents', data),

  revokeConsent: (subjectId: string, consentType: string) =>
    apiClient.delete(`/m6/pipl/consents/${subjectId}/${consentType}`),
};

// ─── M6 Report Schedules ──────────────────────────────────
export interface ReportSchedule {
  id: string;
  tenantId: string;
  createdBy: string;
  name: string;
  reportType: string;
  frequency: string;
  format: string;
  recipientEmails: string[];
  isEnabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const reportScheduleApi = {
  list: () =>
    apiClient.get<ReportSchedule[]>('/report-schedules'),

  create: (data: { name: string; reportType: string; frequency: string; format?: string; recipientEmails?: string[] }) =>
    apiClient.post<ReportSchedule>('/report-schedules', data),

  update: (id: string, data: Partial<Pick<ReportSchedule, 'name' | 'reportType' | 'frequency' | 'format' | 'recipientEmails' | 'isEnabled'>>) =>
    apiClient.patch<ReportSchedule>(`/report-schedules/${id}`, data),

  toggle: (id: string) =>
    apiClient.post<ReportSchedule>(`/report-schedules/${id}/toggle`),

  delete: (id: string) =>
    apiClient.delete(`/report-schedules/${id}`),
};

// ─── M7 Sales Order ────────────────────────────────────────
export interface OrderItemJson {
  productId: string;
  productName: string;
  productNameEn?: string;
  sku?: string;
  unitPrice: number;
  currency: string;
  quantity: number;
  unit?: string;
  subtotal: number;
  specifications?: Record<string, string>;
  imageUrl?: string;
}

export interface SalesOrder {
  id: string;
  orderNo: string;
  source: string;
  contractId: string | null;
  customerId: string | null;
  guestName: string | null;
  guestEmail: string | null;
  guestPhone: string | null;
  guestCompany: string | null;
  status: string;
  currency: string;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  shippingAmount: number;
  totalAmount: number;
  paymentMethod: string | null;
  paymentStatus: string;
  depositRatio: number;
  depositAmount: number;
  depositPaidAt: string | null;
  balanceAmount: number;
  balancePaidAt: string | null;
  fulfillmentTemplate: string;
  balanceTiming: string;
  balanceDueDate: string | null;
  retentionRatio: number;
  deliveryTerms: string | null;
  expectedShipDate: string | null;
  expectedDeliveryDate: string | null;
  itemsJson: OrderItemJson[] | null;
  shippingAddressJson: Record<string, string> | null;
  ownerId: string | null;
  specialRequirements: string | null;
  internalNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const salesOrderApi = {
  list: (params?: ListParams & { status?: string; customerId?: string; ownerId?: string }) =>
    apiClient.get<PaginatedResponse<SalesOrder>>('/m7/sales-orders', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<SalesOrder>>(`/m7/sales-orders/${id}`),

  create: (data: {
    currency: string;
    items: Array<{ productId: string; productName: string; unitPrice: number; currency: string; quantity: number; subtotal?: number }>;
    customerId?: string;
    guestName?: string;
    guestEmail?: string;
    paymentMethod?: string;
    depositRatio?: number;
    fulfillmentTemplate?: string;
    balanceTiming?: string;
    balanceDueDate?: string;
    retentionRatio?: number;
    deliveryTerms?: string;
    specialRequirements?: string;
    shippingAddressJson?: Record<string, string>;
  }) =>
    apiClient.post<SingleResponse<SalesOrder>>('/m7/sales-orders', data),

  update: (id: string, data: Partial<SalesOrder>) =>
    apiClient.patch<SingleResponse<SalesOrder>>(`/m7/sales-orders/${id}`, data),

  updateStatus: (id: string, status: string) =>
    apiClient.patch<SingleResponse<SalesOrder>>(`/m7/sales-orders/${id}/status`, { status }),

  confirmPayment: (id: string, paymentType: 'DEPOSIT' | 'BALANCE' | 'FULL') =>
    apiClient.post<SingleResponse<SalesOrder>>(`/m7/sales-orders/${id}/confirm-payment`, { paymentType }),

  delete: (id: string) =>
    apiClient.delete(`/m7/sales-orders/${id}`),
};

// ─── M8 Quality Control ────────────────────────────────────

export interface QcInspection {
  id: string;
  inspectionNo: string;
  salesOrderId: string | null;
  poId: string | null;
  supplierId: string | null;
  inspectorId: string | null;
  inspectorType: string;
  inspectionDate: string | null;
  inspectionType: string;
  status: string;
  result: string | null;
  samplingQty: number | null;
  totalQty: number | null;
  defectQty: number | null;
  criticalDefects: number;
  majorDefects: number;
  minorDefects: number;
  aqlLevel: string | null;
  reportUrl: string | null;
  notes: string | null;
  photosJson: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export const qcApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string; salesOrderId?: string }) =>
    apiClient.get<{ data: QcInspection[]; meta: { total: number; page: number; pageSize: number } }>('/m8/inspections', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<QcInspection>>(`/m8/inspections/${id}`),

  create: (data: {
    salesOrderId?: string;
    poId?: string;
    supplierId?: string;
    inspectionType: string;
    inspectorType: string;
    inspectionDate?: string;
    totalQty?: number;
    samplingQty?: number;
    aqlLevel?: string;
    notes?: string;
  }) =>
    apiClient.post<SingleResponse<QcInspection>>('/m8/inspections', data),

  update: (id: string, data: Partial<QcInspection>) =>
    apiClient.patch<SingleResponse<QcInspection>>(`/m8/inspections/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m8/inspections/${id}`),
};

// ─── M9 Logistics & Shipping ───────────────────────────────

export interface Shipment {
  id: string;
  qrToken: string;
  shipmentNo: string;
  salesOrderId: string | null;
  poId: string | null;
  supplierId: string | null;
  shippingMethod: string;
  carrierName: string | null;
  trackingNumber: string | null;
  blNumber: string | null;
  awbNumber: string | null;
  portOfLoading: string | null;
  portOfDischarge: string | null;
  placeOfDelivery: string | null;
  etd: string | null;
  eta: string | null;
  atd: string | null;
  ata: string | null;
  freightAmount: number | null;
  freightCurrency: string;
  incoterms: string | null;
  status: string;
  customsStatus: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const shipmentApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string; salesOrderId?: string }) =>
    apiClient.get<{ data: Shipment[]; meta: { total: number; page: number; pageSize: number } }>('/m9/shipments', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<Shipment>>(`/m9/shipments/${id}`),

  create: (data: {
    salesOrderId?: string;
    poId?: string;
    supplierId?: string;
    shippingMethod: string;
    carrierName?: string;
    trackingNumber?: string;
    blNumber?: string;
    awbNumber?: string;
    portOfLoading?: string;
    portOfDischarge?: string;
    placeOfDelivery?: string;
    etd?: string;
    eta?: string;
    freightAmount?: number;
    freightCurrency?: string;
    incoterms?: string;
    notes?: string;
  }) =>
    apiClient.post<SingleResponse<Shipment>>('/m9/shipments', data),

  updateStatus: (id: string, status: string) =>
    apiClient.patch<SingleResponse<Shipment>>(`/m9/shipments/${id}/status`, { status }),

  update: (id: string, data: Partial<Shipment>) =>
    apiClient.patch<SingleResponse<Shipment>>(`/m9/shipments/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/m9/shipments/${id}`),

  trackEvents: (id: string) =>
    apiClient.get<{ data: TrackResult }>(`/m9/shipments/${id}/track-events`),
};

export interface TrackEvent {
  time: string;
  location: string;
  description: string;
}

export interface TrackResult {
  trackingNumber: string;
  carrier: string | null;
  currentStatus: string;
  currentStatusCode: number;
  events: TrackEvent[];
  lastUpdatedAt: string | null;
  error: string | null;
}

// ─── M10 Digital SDR ───────────────────────────────────────

export interface SearchStrategy {
  id: string;
  name: string;
  keywordsJson: string[];
  targetRegionsJson: string[] | null;
  isActive: boolean;
  dailyLimit: number;
  minScore: number;
  runSchedule: string;
  lastRunAt: string | null;
  totalLeadsFound: number;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DiscoveryRun {
  id: string;
  strategyId: string;
  strategyName: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  startedAt: string;
  completedAt: string | null;
  urlsSearched: number;
  leadsFound: number;
  leadsQualified: number;
  errorMessage: string | null;
  triggerType: 'SCHEDULED' | 'MANUAL';
  createdAt: string;
}

export interface DiscoveredLead {
  reviewQuality?: { maxScore: number; issues: string[] };
  id: string;
  runId: string;
  strategyId: string;
  sourceUrl: string;
  sourceDomain: string | null;
  searchQuery: string | null;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  contactPerson: string | null;
  country: string | null;
  city: string | null;
  industry: string | null;
  companySize: string | null;
  mainProducts: string | null;
  buyingSignals: string | null;
  aiSummary: string | null;
  score: number;
  scoreFactorsJson: string[] | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'IMPORTED' | 'DUPLICATE';
  crmLeadId: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export interface SdrStats {
  totalLeads: number;
  pendingLeads: number;
  approvedLeads: number;
  importedLeads: number;
  avgScore: number;
  recentRuns: DiscoveryRun[];
}

export interface SdrReadiness {
  usable: boolean;
  checkedExternal: boolean;
  discovery: {
    activeStrategyCount: number;
    llmConfigured: boolean;
    search: {
      ok: boolean | null;
      provider: string;
      resultCount: number | null;
      errors: string[];
    };
  };
  outreach: {
    activeCategorySequenceCount: number;
    categorySequenceStepCount: number;
    activeReplyAccountCount: number;
    healthyReplyAccountCount: number;
    delivery: {
      configured: boolean;
      verified: boolean | null;
      error?: string;
      unsubscribeConfigured: boolean;
      baseUrl: string;
    };
  };
  blockers: string[];
  warnings: string[];
}

export interface SdrImportResult {
  lead: DiscoveredLead;
  outreach: {
    enrolled: boolean;
    sequenceId?: string;
    reason?: string;
  };
}

// Backend listLeads response shape (not wrapped in { data: ... })
interface LeadsListResponse {
  data: DiscoveredLead[];
  meta: { total: number; page: number; pageSize: number };
}

export interface DepartmentOperations {
  capturedAt: string;
  approvalRequired: boolean;
  pendingLeads: number;
  approvedLeads: number;
  activeEnrollments: number;
  dueEnrollments: number;
  pendingEmails: number;
  failedEnrollments: number;
  sentEmails7d: number;
  inboundMessages7d: number;
  replyDrafts7d: number;
  activeMailboxes: number;
  freshMailboxes: number;
  activeSocialAccounts: number;
  staleRuns: number;
  latestRun: { status: string; startedAt: string; completedAt: string | null; urlsSearched: number; leadsFound: number } | null;
  scope: string;
}

export const digitalSdrApi = {
  getOperations: () => apiClient.get<DepartmentOperations>('/m10/sdr/operations'),
  prepareDrafts: () => apiClient.post<{ processed: number; errors: number }>('/m10/sdr/prepare-drafts', {}),
  // Stats — backend returns SdrStats directly (no { data: } wrapper)
  getStats: () =>
    apiClient.get<SdrStats>('/m10/sdr/stats'),

  getReadiness: (verifyExternal = false) =>
    apiClient.get<SdrReadiness>('/m10/sdr/readiness', {
      params: { verifyExternal },
    }),

  listRuns: (params?: { page?: number; pageSize?: number }) =>
    apiClient.get<{ data: DiscoveryRun[]; meta: { total: number; page: number; pageSize: number } }>('/m10/sdr/runs', { params }),

  runStrategy: (strategyId: string) =>
    apiClient.post<DiscoveryRun>(`/m10/sdr/run/${strategyId}`, {}),

  runAll: () =>
    apiClient.post<{ message: string }>('/m10/sdr/run-all', {}),

  // Leads — backend returns { data: [...], meta: {...} } directly
  listLeads: (params?: { page?: number; pageSize?: number; status?: string; minScore?: number }) =>
    apiClient.get<LeadsListResponse>('/m10/sdr/leads', { params }),

  getLeadById: (id: string) =>
    apiClient.get<DiscoveredLead>(`/m10/sdr/leads/${id}`),

  approveLead: (id: string, reviewNote?: string) =>
    apiClient.post<DiscoveredLead>(`/m10/sdr/leads/${id}/approve`, { reviewNote }),

  rejectLead: (id: string, reviewNote?: string) =>
    apiClient.post<DiscoveredLead>(`/m10/sdr/leads/${id}/reject`, { reviewNote }),

  importToCrm: (id: string) =>
    apiClient.post<SdrImportResult>(`/m10/sdr/leads/${id}/import`, {}),

  // Strategies — backend returns SearchStrategyEntity[] directly
  listStrategies: () =>
    apiClient.get<SearchStrategy[]>('/m10/strategies'),

  createStrategy: (data: Partial<SearchStrategy>) =>
    apiClient.post<SearchStrategy>('/m10/strategies', data),

  updateStrategy: (id: string, data: Partial<SearchStrategy>) =>
    apiClient.patch<SearchStrategy>(`/m10/strategies/${id}`, data),

  deleteStrategy: (id: string) =>
    apiClient.delete(`/m10/strategies/${id}`),
};

// ─── Inventory Management ──────────────────────────────────
export interface InventoryItem {
  id: string;
  productId: string;
  productName: string;
  modelNumber?: string | null;
  brand?: string | null;
  supplierId?: string | null;
  supplierName?: string | null;
  warehouseId: string;
  warehouseName: string;
  warehouseCode?: string;
  countryCode?: string;
  batchNo: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  unitCost: number;
  currency?: string;
  inventoryValue: number;
  lastReceiptDate: string;
  lastShipmentDate: string;
  status: 'NORMAL' | 'LOW_STOCK' | 'OVERSTOCK';
  createdAt: string;
  updatedAt: string;
  /** Current API entity fields retained alongside legacy normalized names. */
  qtyOnHand?: number;
  qtyReserved?: number;
  binCode?: string | null;
}

export interface InventoryAlert {
  id: string;
  itemId: string;
  productName: string;
  warehouseName: string;
  alertType: 'LOW_STOCK' | 'OVERSTOCK' | 'EXPIRED';
  threshold: number;
  currentQty: number;
  createdAt: string;
}

export interface InventoryReport {
  costVisible: boolean;
  totalInventoryValue: number;
  totalSKU: number;
  lowStockCount: number;
  overstockCount: number;
  totalQuantity: number;
}

export interface InventoryReceipt {
  id?: string;
  warehouseId: string;
  productId: string;
  batchNo?: string;
  quantityReceived: number;
  unitCost: number;
  receivedDate: string;
  notes?: string;
  createdAt?: string;
}

export interface Warehouse {
  id: string;
  code?: string;
  name: string;
  areaLetter?: string | null;
  notes?: string | null;
  countryCode?: string;
  legalEntity?: string;
  baseCurrency?: string;
  location: string;
  capacity: number;
  manager: string;
  contact: string;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface InventorySummaryRegion {
  warehouseId: string; warehouseName?: string; areaLetter?: string | null;
  countryCode?: string; legalEntity?: string; batchNo?: string | null; binCode?: string | null;
  quantityOnHand: number; quantityReserved: number; quantityAvailable: number;
  unitCost: number; currency?: string;
}
export interface InventorySummaryRow {
  productId: string; legalEntity: string; productName: string; modelNumber: string; brand: string;
  totalOnHand: number; totalReserved: number; totalAvailable: number;
  regions: InventorySummaryRegion[];
}

export const inventoryApi = {
  getInventorySummary: (params?: { warehouseId?: string; productId?: string; lowStockOnly?: boolean; q?: string; brand?: string; modelNumber?: string; binCode?: string }) =>
    apiClient.get<{ data: InventorySummaryRow[] }>('/inventory/summary', { params }),

  list: (params?: {
    page?: number; pageSize?: number; warehouseId?: string; lowStockOnly?: boolean;
    q?: string; brand?: string; modelNumber?: string; binCode?: string; supplierId?: string;
  }) =>
    apiClient.get<PaginatedResponse<InventoryItem>>('/inventory', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<InventoryItem>>(`/inventory/${id}`),

  getAlerts: (params?: { warehouseId?: string; q?: string; brand?: string; modelNumber?: string; binCode?: string }) =>
    apiClient.get<InventoryAlert[]>('/inventory/alerts', { params }),

  getReport: (params?: { warehouseId?: string; lowStockOnly?: boolean; q?: string; brand?: string; modelNumber?: string; binCode?: string }) =>
    apiClient.get<SingleResponse<InventoryReport>>('/inventory/report/value', { params }),

  getWarehouses: () =>
    apiClient.get<Warehouse[]>('/inventory/warehouses'),

  createWarehouse: (data: { code: string; name: string; areaLetter?: string; countryCode: string; legalEntity: string; baseCurrency: string; address?: string }) =>
    apiClient.post<SingleResponse<Warehouse>>('/inventory/warehouses', data),

  updateWarehouse: (id: string, data: { name?: string; notes?: string | null; address?: string | null; areaLetter?: string }) =>
    apiClient.patch<SingleResponse<Warehouse>>(`/inventory/warehouses/${id}`, data),

  deactivateWarehouse: (id: string) =>
    apiClient.post<SingleResponse<{ deactivatedWarehouseId: string }>>(`/inventory/warehouses/${id}/deactivate`, {}),

  receipt: (data: InventoryReceipt) =>
    apiClient.post<SingleResponse<InventoryItem>>('/inventory/receipt', data),

  shipment: (data: { itemId: string; quantity: number; shipmentDate: string; notes?: string }) =>
    apiClient.post<SingleResponse<InventoryItem>>('/inventory/shipment', data),

  updateCost: (id: string, unitCost: number) =>
    apiClient.patch<SingleResponse<InventoryItem>>(`/inventory/${id}/cost`, { unitCost }),

  // 按产品查询全部库存流水（PURCHASE_IN / SALE_OUT / ADJUSTMENT / TRANSFER / RETURN）
  // 用于"采购入库"视图反查"这次进货补到了哪些库存"。
  getMovements: (productId: string, warehouseId?: string) =>
    apiClient.get<SingleResponse<Array<{
      id: string;
      type: string;
      productId: string;
      warehouseId: string;
      qty: number;
      unitCost: number;
      refType: string | null;
      refId: string | null;
      refNo: string | null;
      notes: string | null;
      createdAt: string;
    }>>>(`/inventory/${productId}/movements`, { params: { warehouseId } }),

  getProductFlow: (productId: string) =>
    apiClient.get<SingleResponse<{
      productId: string;
      stages: { purchased: number; inTransit: number; received: number; issued: number; delivered: number };
      purchaseOrders: Array<Record<string, unknown>>;
      receipts: Array<Record<string, unknown>>;
      movements: Array<Record<string, unknown>>;
      salesOrders: Array<Record<string, unknown>>;
    }>>(`/inventory/${productId}/flow`),

  // 库存盘点调整：设定库存项的新实物数量，自动记录 ADJUSTMENT 流水
  adjust: (data: { inventoryItemId: string; newQty: number; reason: string; notes?: string }) =>
    apiClient.post('/inventory/adjust', data),
};

// ─── M11 Goods Receipts (入库单, 进销存 Phase-1) ───────────────

export interface GoodsReceiptItem {
  id: string;
  productId: string | null;
  matchType: string;
  modelNumber: string | null;
  brand: string | null;
  productName: string;
  quantity: number;
  unit: string;
  unitCost: number;
  binCode: string | null;
  batchNo: string | null;
  passportCodes: string[] | null;
  minSalePrice: number | null;
  notes: string | null;
  sortOrder: number;
}

export interface GoodsReceipt {
  id: string;
  qrToken: string;
  receiptNo: string;
  status: string;
  source: string;
  poId: string | null;
  supplierId: string | null;
  supplierName: string | null;
  warehouseId: string | null;
  currency: string;
  purchaseDate: string | null;
  fxRateToCny: number | null;
  fxRateSource: string | null;
  deliveryNoteNo: string | null;
  confirmedAt: string | null;
  notes: string | null;
  items?: GoodsReceiptItem[];
  createdAt: string;
}

export const goodsReceiptApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string }) =>
    apiClient.get<PaginatedResponse<GoodsReceipt>>('/m11/goods-receipts', { params }),
  getById: (id: string) =>
    apiClient.get<SingleResponse<GoodsReceipt>>(`/m11/goods-receipts/${id}`),
  create: (data: { source?: string; supplierId?: string; supplierName?: string; warehouseId?: string; currency?: string; purchaseDate?: string; fxRateToCny?: number; fxRateSource?: string; deliveryNoteNo?: string; notes?: string; items: Array<Partial<GoodsReceiptItem>> }) =>
    apiClient.post<SingleResponse<GoodsReceipt>>('/m11/goods-receipts', data),
  fromPo: (poId: string) =>
    apiClient.post<SingleResponse<GoodsReceipt>>(`/m11/goods-receipts/from-po/${poId}`),
  importExcel: (file: File, meta: { supplierId?: string; supplierName?: string; warehouseId?: string; defaultBinCode?: string; currency?: string; deliveryNoteNo?: string; purchaseDate?: string; fxRateToCny?: number; fxRateSource?: string }) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(meta).forEach(([k, v]) => { if (v) fd.append(k, String(v)); });
    return apiClient.post<SingleResponse<GoodsReceipt>>('/m11/goods-receipts/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadTemplate: () =>
    apiClient.get('/m11/goods-receipts/template', { responseType: 'blob' }),
  updateItems: (id: string, items: Array<{ id: string } & Partial<GoodsReceiptItem>>) =>
    apiClient.patch<SingleResponse<GoodsReceipt>>(`/m11/goods-receipts/${id}/items`, { items }),
  updateDetails: (id: string, data: Partial<Pick<GoodsReceipt, 'warehouseId' | 'currency' | 'purchaseDate' | 'fxRateToCny' | 'fxRateSource' | 'deliveryNoteNo' | 'notes'>>) =>
    apiClient.patch<SingleResponse<GoodsReceipt>>(`/m11/goods-receipts/${id}`, data),
  confirm: (id: string) =>
    apiClient.post<SingleResponse<GoodsReceipt>>(`/m11/goods-receipts/${id}/confirm`),
  cancel: (id: string) =>
    apiClient.post<SingleResponse<GoodsReceipt>>(`/m11/goods-receipts/${id}/cancel`),
};

// ─── M11 Internal project cost loop ─────────────────────────

export interface InternalProject {
  id: string;
  projectCode: string;
  companyCode: string;
  name: string;
  projectType: 'INTERNAL' | 'CUSTOMER_SERVICE' | 'CUSTOMER_DELIVERY' | 'R_AND_D' | 'OTHER';
  legalEntity: 'CN' | 'VN';
  baseCurrency: 'CNY';
  status: 'ACTIVE' | 'CLOSED' | 'CANCELLED';
  customerId: string | null;
  contractId: string | null;
  salesOrderId: string | null;
  ownerId: string | null;
  budgetAmountCny: number;
  notes: string | null;
  createdAt: string;
}

export interface ProjectCostEntry {
  id: string;
  projectId: string;
  projectCode: string;
  entryType: 'MATERIAL_ISSUE' | 'MATERIAL_RETURN' | 'SERVICE_ESTIMATE' | 'SERVICE_ACTUAL' | 'ADJUSTMENT';
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'POSTED' | 'CANCELLED';
  productId: string | null;
  description: string;
  purchaseDate: string;
  sourceCurrency: 'CNY' | 'VND' | 'USD';
  sourceAmount: number;
  fxRateToCny: number;
  amountCny: number;
  requestedBy: string;
  approvedBy: string | null;
  createdAt: string;
}

export interface ProjectCostSummary {
  project: InternalProject;
  actualCny: number;
  estimatedCny: number;
  budgetAmountCny: number;
  overBudget: boolean;
  entries: ProjectCostEntry[];
}

export interface ProjectCostOverview {
  projectCount: number;
  activeProjectCount: number;
  overBudgetCount: number;
  totalActualCny: number;
  totalEstimatedCny: number;
  topProjects: Array<{
    id: string; projectCode: string; name: string; legalEntity: 'CN' | 'VN'; status: string;
    budgetAmountCny: number; actualCny: number; estimatedCny: number; overBudget: boolean;
  }>;
}

export interface IssueNoteItem {
  id: string;
  originalIssueItemId: string | null;
  productId: string;
  productName: string;
  brand: string | null;
  quantity: number;
  unit: string;
  unitCostCny: number | null;
  subtotalCostCny: number | null;
  passportCodes: string[] | null;
  sourceBinCode: string | null;
  sortOrder: number;
}

export interface IssueNote {
  id: string;
  issueNo: string;
  documentType: 'ISSUE' | 'RETURN';
  originalIssueId: string | null;
  projectId: string;
  projectCode: string;
  warehouseId: string;
  legalEntity: 'CN' | 'VN';
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'CONFIRMED' | 'CANCELLED';
  purpose: string | null;
  totalCostCny: number | null;
  requestedBy: string;
  approvedBy: string | null;
  approvedAt: string | null;
  overBudget: boolean;
  qrToken: string;
  dpsSyncStatus: string;
  items?: IssueNoteItem[];
  createdAt: string;
}

export const internalProjectApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string; legalEntity?: string; search?: string }) =>
    apiClient.get<PaginatedResponse<InternalProject>>('/m11/projects', { params }),
  getById: (id: string) => apiClient.get<InternalProject>(`/m11/projects/${id}`),
  create: (data: Partial<InternalProject>) => apiClient.post<InternalProject>('/m11/projects', data),
  update: (id: string, data: Partial<InternalProject>) => apiClient.patch<InternalProject>(`/m11/projects/${id}`, data),
  summary: (id: string) => apiClient.get<SingleResponse<ProjectCostSummary>>(`/m11/projects/${id}/cost-summary`),
  overview: () => apiClient.get<SingleResponse<ProjectCostOverview>>('/m11/projects/cost-overview'),
  createCost: (id: string, data: Partial<ProjectCostEntry>) => apiClient.post<ProjectCostEntry>(`/m11/projects/${id}/costs`, data),
  submitCost: (id: string) => apiClient.post<ProjectCostEntry>(`/m11/projects/costs/${id}/submit`),
  approveCost: (id: string, data: { notes?: string; overrideReason?: string } = {}) =>
    apiClient.post<ProjectCostEntry>(`/m11/projects/costs/${id}/approve`, data),
};

export const issueNoteApi = {
  list: (params?: { page?: number; pageSize?: number; projectId?: string; status?: string; documentType?: string }) =>
    apiClient.get<PaginatedResponse<IssueNote>>('/m11/issue-notes', { params }),
  getById: (id: string) => apiClient.get<IssueNote>(`/m11/issue-notes/${id}`),
  create: (data: { projectId: string; warehouseId: string; crossRegion?: boolean; purpose?: string; items: Array<{ productId: string; quantity: number; unit?: string; passportCodes?: string[]; sourceBinCode?: string }> }) =>
    apiClient.post<IssueNote>('/m11/issue-notes', data),
  createReturn: (id: string, data: { purpose?: string; items: Array<{ originalIssueItemId: string; quantity: number; passportCodes?: string[] }> }) =>
    apiClient.post<IssueNote>(`/m11/issue-notes/${id}/returns`, data),
  submit: (id: string) => apiClient.post<IssueNote>(`/m11/issue-notes/${id}/submit`),
  approve: (id: string, data: { notes?: string; overrideReason?: string } = {}) => apiClient.post<IssueNote>(`/m11/issue-notes/${id}/approve`, data),
  confirm: (id: string) => apiClient.post<IssueNote>(`/m11/issue-notes/${id}/confirm`),
  cancel: (id: string) => apiClient.post<IssueNote>(`/m11/issue-notes/${id}/cancel`),
  retryDps: (id: string) => apiClient.post<IssueNote>(`/m11/issue-notes/${id}/retry-dps`),
};

// ─── M11 BOM Match + Bin lookup (进销存 Phase-2) ──────────────

export interface BomMatchRow {
  input: { modelNumber?: string; productName?: string; quantity: number };
  matchType: 'MATCHED_SKU' | 'MATCHED_NAME' | 'UNMATCHED';
  productId: string | null;
  productName: string | null;
  binCode: string | null;
  supplierId: string | null;
  onHand: number;
  afterIssue: number;
  shortage: number;
  unitCost: number;
}

export interface BomMatchReport {
  rows: BomMatchRow[];
  summary: { total: number; matched: number; shortageLines: number; unmatched: number };
}

export const bomApi = {
  downloadTemplate: () => apiClient.get('/m11/bom/template', { responseType: 'blob' }),
  match: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<SingleResponse<BomMatchReport>>('/m11/bom/match', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  shortageToPo: (rows: BomMatchRow[]) =>
    apiClient.post<SingleResponse<Array<{ orderNo: string; supplierName: string; lineCount: number; totalAmount: number }>>>(
      '/m11/bom/shortage-to-po', { rows }),
};

export const binApi = {
  lookup: (binCode: string) =>
    apiClient.get<SingleResponse<{ bin: {
      id: string; binCode: string; countryCode: string; areaCode: string; rackNo: number;
      levelNo: number; positionNo: number; rackRemark?: string | null; notes?: string | null; qrUrl: string; warehouseId: string;
      warehouseName?: string; warehouseCode?: string;
    }; binCode: string; items: Array<{
      inventoryItemId: string;
      productId: string; productName: string | null; modelNumber: string | null; brand: string | null;
      batchNo: string | null; binCode: string | null;
      qtyOnHand: number; qtyReserved: number; unitCost: number; supplierId: string | null;
      passportCodes: string[] | null;
    }> }>>(`/inventory/bin/${encodeURIComponent(binCode)}`),
  locate: (q: string, warehouseId?: string) => apiClient.get<SingleResponse<Array<{
    id: string; productId: string; modelNumber: string | null; brand: string | null; productName: string;
    warehouseId: string; warehouseCode: string; warehouseName: string; countryCode: string;
    binCode: string | null; batchNo: string | null; qtyOnHand: number; qtyReserved: number; qtyAvailable: number;
  }>>>('/inventory/locator/search', { params: { q, warehouseId } }),
  racks: () => apiClient.get<SingleResponse<Array<{
    id: string; warehouseId: string; warehouseCode: string; warehouseName: string; countryCode: string;
    areaCode: string; rackNo: number; levelCount: number; positionCount: number; binCount: number;
    remark: string | null; isActive: boolean;
  }>>>('/inventory/bins/racks'),
  suggest: (q: string, warehouseId?: string) => apiClient.get<SingleResponse<Array<{
    id: string; binCode: string; notes: string | null; rackRemark: string | null;
    warehouseId: string; warehouseCode: string; warehouseName: string;
  }>>>('/inventory/bins/suggest', { params: { q, warehouseId } }),
  configureRack: (data: {
    warehouseId: string; countryCode: string; areaCode: string; rackNo: number;
    levelCount: number; positionCount: number; remark?: string;
  }) => apiClient.post('/inventory/bins/configure-rack', data),
  deactivateRack: (id: string) => apiClient.post(`/inventory/bins/racks/${id}/deactivate`),
  deactivate: (id: string) => apiClient.post(`/inventory/bins/${id}/deactivate`),
  updateNotes: (id: string, notes?: string | null) =>
    apiClient.patch(`/inventory/bins/${id}/notes`, { notes }),
  moveInventory: (inventoryItemId: string, toBinCode: string) =>
    apiClient.post('/inventory/bins/move-inventory', { inventoryItemId, toBinCode }),
  verifySerials: (productId: string, codes: string[]) =>
    apiClient.post<SingleResponse<{ valid: boolean; notBound: string[] }>>('/inventory/verify-serials', { productId, codes }),
};

// ─── M11 Physical asset ledger ─────────────────────────────
export type AssetStatus = 'PENDING_REGISTRATION' | 'REJECTED' | 'IN_STOCK' | 'ASSIGNED' | 'IN_REPAIR' | 'LOST' | 'DISPOSED';
export type AssetEventType = 'INBOUND' | 'ASSIGN' | 'RETURN' | 'TRANSFER' | 'REPAIR_OUT' | 'REPAIR_IN' | 'STOCKTAKE' | 'MARK_LOST' | 'DISPOSE';

export interface AssetEvent {
  id: string; eventNo: string; assetId: string; eventType: AssetEventType;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  fromStatus: AssetStatus | null; toStatus: AssetStatus | null;
  fromAssigneeId: string | null; toAssigneeId: string | null;
  fromWarehouseId: string | null; toWarehouseId: string | null;
  fromBinCode: string | null; toBinCode: string | null;
  location: string | null; counterparty: string | null; reason: string | null;
  eventAt: string; requestedBy: string; approvedBy: string | null; approvedAt: string | null;
  approvalComment: string | null;
  requester?: { id: string; displayName: string };
  approver?: { id: string; displayName: string } | null;
  toAssignee?: { id: string; displayName: string; email: string } | null;
}

export interface AssetCard {
  id: string; assetCode: string; qrToken: string; legalEntity: string;
  managementCategory: 'FIXED_ASSET' | 'LOW_VALUE_ASSET'; productId: string | null;
  assetName: string; brand: string | null; modelNumber: string | null; serialNumber: string | null;
  warehouseId: string | null; binCode: string | null; currentLocation: string | null;
  currentAssigneeId: string | null; assignedAt: string | null; acquiredAt: string;
  originalValue: number; currency: string; baseValue: number; baseCurrency: string; capitalized: boolean; status: AssetStatus;
  lastStocktakeAt: string | null; lastEventAt: string | null; notes: string | null;
  warehouse?: { id: string; code: string; name: string; countryCode: string } | null;
  currentAssignee?: { id: string; displayName: string; email: string; isActive: boolean } | null;
  events?: AssetEvent[];
}

export interface AssetPolicy {
  id: string; legalEntity: string; capitalizationThreshold: number; baseCurrency: string;
  assetCodePrefix: string; isActive: boolean;
}

export interface PublicAssetTrace {
  assetCode: string; ownership: string; assetName: string; brand: string | null; modelNumber: string | null;
  currentUser: string | null; assignedAt: string | null; goodsValue: number; currency: string;
  status: AssetStatus; currentLocation: string | null; lastStocktakeAt: string | null;
  lastEventAt: string | null; canonicalUrl: string;
}

export const assetApi = {
  list: (params?: { search?: string; status?: string; legalEntity?: string; assigneeId?: string; page?: number; pageSize?: number }) =>
    apiClient.get<PaginatedResponse<AssetCard>>('/assets', { params }),
  detail: (id: string) => apiClient.get<SingleResponse<AssetCard>>(`/assets/${id}`),
  fullTrace: (token: string) => apiClient.get<SingleResponse<AssetCard>>(`/assets/qr/${token}`),
  publicTrace: (token: string) => apiClient.get<SingleResponse<PublicAssetTrace>>(`/assets/public/${token}`),
  create: (data: Partial<AssetCard> & {
    legalEntity: string; warehouseId: string; acquiredAt: string; originalValue: number; initialAssigneeId?: string;
  }) =>
    apiClient.post('/assets', data),
  createEvent: (id: string, data: { eventType: AssetEventType; toAssigneeId?: string; toWarehouseId?: string; toBinCode?: string; location?: string; counterparty?: string; reason?: string; eventAt?: string }) =>
    apiClient.post<SingleResponse<AssetEvent>>(`/assets/${id}/events`, data),
  approveEvent: (eventId: string, comment?: string) => apiClient.post(`/assets/events/${eventId}/approve`, { comment }),
  rejectEvent: (eventId: string, comment: string) => apiClient.post(`/assets/events/${eventId}/reject`, { comment }),
  policies: () => apiClient.get<SingleResponse<AssetPolicy[]>>('/assets/policies'),
  assignees: (search?: string) => apiClient.get<SingleResponse<Array<{ id: string; displayName: string; email: string; role: string }>>>('/assets/assignees', { params: { search } }),
  upsertPolicy: (legalEntity: string, data: { capitalizationThreshold: number; baseCurrency: string; assetCodePrefix?: string }) =>
    apiClient.put<SingleResponse<AssetPolicy>>(`/assets/policies/${legalEntity}`, data),
};

// ─── Finance Management ────────────────────────────────────
export interface FinanceDashboardStats {
  currency: string;
  availableCurrencies: string[];
  ar: { total_receivable: number; total_received: number; outstanding_ar: number; overdue_ar_count: number };
  ap: { total_payable: number; total_paid: number; outstanding_ap: number; overdue_ap_count: number };
  netCashFlow: number;
}

export interface FinanceLedgerList<T> { items: T[]; total: number; page: number; limit: number }

export interface AccountsReceivable {
  currency: string;
  totalAmount?: number | string;
  receivedAmount?: number | string;
  id: string;
  invoiceNo: string;
  customerId: string;
  customerName: string;
  amount: number;
  amountReceived: number;
  amountOutstanding: number;
  dueDate: string;
  overdueDays: number;
  status: 'PENDING' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  invoiceDate: string;
  paymentTerms: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AccountsPayable {
  currency: string;
  totalAmount?: number | string;
  paidAmount?: number | string;
  id: string;
  poNo: string;
  supplierId: string;
  supplierName: string;
  amount: number;
  amountPaid: number;
  amountOutstanding: number;
  dueDate: string;
  overdueDays: number;
  status: 'PENDING' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  poDate: string;
  paymentTerms: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentRecord {
  currency?: string;
  id?: string;
  arId?: string;
  apId?: string;
  amount: number;
  paymentDate: string;
  paymentMethod: 'BANK_TRANSFER' | 'CHECK' | 'CASH' | 'CREDIT_CARD' | 'OTHER';
  referenceNo?: string;
  notes?: string;
  createdAt?: string;
}

// ─── M3 Extended Types & APIs ──────────────────────────────

export interface CourseContent {
  id: string;
  slug: string;
  titleZh: string;
  titleEn: string;
  descriptionZh?: string | null;
  descriptionEn?: string | null;
  category: string;
  difficulty: string;
  durationMinutes?: number | null;
  thumbnailUrl?: string | null;
  videoUrl?: string | null;
  bilibiliUrl?: string | null;
  youtubeUrl?: string | null;
  localVideoUrl?: string | null;
  documentUrl?: string | null;
  contentZh?: string | null;
  contentEn?: string | null;
  isPublic: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const courseApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<CourseContent>>('/m3/courses', { params }),
  getById: (id: string) =>
    apiClient.get<SingleResponse<CourseContent>>(`/m3/courses/${id}`),
  create: (data: Partial<CourseContent>) =>
    apiClient.post<SingleResponse<CourseContent>>('/m3/courses', data),
  update: (id: string, data: Partial<CourseContent>) =>
    apiClient.put<SingleResponse<CourseContent>>(`/m3/courses/${id}`, data),
  delete: (id: string) =>
    apiClient.delete(`/m3/courses/${id}`),
};

export interface Job {
  id: string;
  slug: string;
  titleZh: string;
  titleEn: string;
  department: string;
  location: string;
  jobType: string;
  descriptionZh: string;
  descriptionEn: string;
  requirementsZh: string;
  requirementsEn: string;
  salaryRange?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface JobApplication {
  id: string;
  jobId: string;
  name: string;
  email: string;
  phone?: string | null;
  resumeUrl?: string | null;
  coverLetter?: string | null;
  status: string;
  reviewerNotes?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const jobApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<Job>>('/m3/jobs', { params }),
  getById: (id: string) =>
    apiClient.get<SingleResponse<Job>>(`/m3/jobs/${id}`),
  create: (data: Partial<Job>) =>
    apiClient.post<SingleResponse<Job>>('/m3/jobs', data),
  update: (id: string, data: Partial<Job>) =>
    apiClient.patch<SingleResponse<Job>>(`/m3/jobs/${id}`, data),
  delete: (id: string) =>
    apiClient.delete(`/m3/jobs/${id}`),
  listApplications: (jobId: string, page?: number, pageSize?: number) =>
    apiClient.get<PaginatedResponse<JobApplication>>(`/m3/jobs/${jobId}/applications`, { params: { page, pageSize } }),
  updateApplicationStatus: (appId: string, status: string, notes?: string) =>
    apiClient.patch(`/m3/jobs/applications/${appId}/status`, { status, notes }),
};

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  subject: string;
  message: string;
  productInterest?: string | null;
  sourcePage?: string | null;
  ipAddress?: string | null;
  /** 'website' (contact form) or 'ai_chat' (AI receptionist) */
  source?: string | null;
  status: string;
  crmLeadId?: string | null;
  repliedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const contactMessageApi = {
  list: (params: ListParams & { source?: string }) =>
    apiClient.get<PaginatedResponse<ContactMessage>>('/m3/contact-messages', { params }),
  getById: (id: string) =>
    apiClient.get<SingleResponse<ContactMessage>>(`/m3/contact-messages/${id}`),
  markRead: (id: string) =>
    apiClient.patch(`/m3/contact-messages/${id}/read`, {}),
  delete: (id: string) =>
    apiClient.delete(`/m3/contact-messages/${id}`),
  unreadCount: () =>
    apiClient.get<{ count: number }>('/m3/contact-messages/unread-count'),
};

/** Sidebar badge counts — unprocessed items per M3 inbox. */
export interface InboxCounts {
  contactsUnread: number;
  aiLeadsNew: number;
  rfqNew: number;
  subscribersNew: number;
}

export const inboxApi = {
  counts: () => apiClient.get<InboxCounts>('/m3/inbox-counts'),
};

export interface ChatMsg { role: string; content: string; createdAt: string }
export interface ChatSessionSummary {
  id: string; sessionId: string; mode: string; pageUrl?: string | null; locale?: string | null;
  agentUserId?: string | null; createdAt: string; updatedAt: string;
  lastVisitorAt?: string | null; lastAgentAt?: string | null; messageCount: number;
  lastMessage?: { role: string; content: string; createdAt: string } | null;
}

export const liveChatApi = {
  sessions: (active = true) =>
    apiClient.get<ChatSessionSummary[]>(`/m3/chat/sessions?active=${active ? 1 : 0}`),
  getSession: (id: string) =>
    apiClient.get<{ session: ChatSessionSummary; messages: ChatMsg[] }>(`/m3/chat/sessions/${id}`),
  poll: (id: string, after?: string) =>
    apiClient.get<{ mode: string; messages: ChatMsg[] }>(`/m3/chat/sessions/${id}/poll${after ? `?after=${encodeURIComponent(after)}` : ''}`),
  takeover: (id: string) => apiClient.post(`/m3/chat/sessions/${id}/takeover`, {}),
  release: (id: string) => apiClient.post(`/m3/chat/sessions/${id}/release`, {}),
  send: (id: string, content: string) => apiClient.post(`/m3/chat/sessions/${id}/agent-message`, { content }),
};

export interface Subscriber {
  id: string;
  email: string;
  locale: string;
  status: string;
  name?: string | null;
  country?: string | null;
  unsubscribedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const subscriberApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<Subscriber>>('/m3/subscribers', { params }),
  getStats: () =>
    apiClient.get('/m3/subscribers/stats'),
  broadcast: (data: { subject: string; html: string; test?: string }) =>
    apiClient.post<{ success: boolean; data: { totalRecipients: number; sent: number; failed: number; skipped: number } }>(
      '/m3/subscribers/broadcast', data,
    ),
};

export interface RfqItem {
  productId?: string;
  productName: string;
  quantity: number;
  unit?: string;
  note?: string;
  model?: string;
  productSlug?: string;
}

export interface RfqRequest {
  id: string;
  rfqNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string | null;
  customerCompany?: string | null;
  country?: string | null;
  submissionKey?: string | null;
  visitorId?: string | null;
  sourcePage?: string | null;
  items: RfqItem[];
  incoterm: string;
  currency: string;
  destinationPort?: string | null;
  notes?: string | null;
  status: string;
  quotedAmount?: number | null;
  quotedAt?: string | null;
  quoteNotificationStatus: 'NOT_SENT' | 'SENT' | 'FAILED';
  quoteNotifiedAt?: string | null;
  quoteNotificationError?: string | null;
  acceptedAt?: string | null;
  crmOpportunityId?: string | null;
  crmLeadId?: string | null;
  tags?: {
    productLines?: string[];
    categoryCodes?: string[];
    primaryLine?: string | null;
    region?: string | null;
    inquiryType?: 'purchase' | 'service' | 'parts' | 'mixed';
    itemCount?: number;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export const rfqApi = {
  list: (params: ListParams) =>
    apiClient.get<PaginatedResponse<RfqRequest>>('/m3/rfq', { params }),
  getById: (id: string) =>
    apiClient.get<SingleResponse<RfqRequest>>(`/m3/rfq/${id}`),
  updateStatus: (id: string, status: string, extra?: { quotedAmount?: number }) =>
    apiClient.patch<RfqRequest>(`/m3/rfq/${id}/status`, { status, ...extra }),
  resendQuote: (id: string) =>
    apiClient.post<RfqRequest>(`/m3/rfq/${id}/resend-quote`),
  delete: (id: string) =>
    apiClient.delete(`/m3/rfq/${id}`),
};

// ─── Finance ───────────────────────────────────────────────

export const financeApi = {
  getDashboard: (currency?: string) =>
    apiClient.get<FinanceDashboardStats>('/finance/dashboard', { params: { currency } }),

  listAR: (params?: { page?: number; pageSize?: number; status?: string; currency?: string }) =>
    apiClient.get<FinanceLedgerList<AccountsReceivable>>('/finance/ar', { params }),

  getARById: (id: string) =>
    apiClient.get<SingleResponse<AccountsReceivable>>(`/finance/ar/${id}`),

  recordARPayment: (id: string, payment: PaymentRecord) =>
    apiClient.post<SingleResponse<AccountsReceivable>>(`/finance/ar/${id}/payment`, payment),

  listAP: (params?: { page?: number; pageSize?: number; status?: string; currency?: string }) =>
    apiClient.get<FinanceLedgerList<AccountsPayable>>('/finance/ap', { params }),

  getAPById: (id: string) =>
    apiClient.get<SingleResponse<AccountsPayable>>(`/finance/ap/${id}`),

  recordAPPayment: (id: string, payment: PaymentRecord) =>
    apiClient.post<SingleResponse<AccountsPayable>>(`/finance/ap/${id}/payment`, payment),

  getAROverdue: (currency?: string) =>
    apiClient.get<FinanceLedgerList<AccountsReceivable>>('/finance/ar', { params: { status: 'overdue', currency, pageSize: 100 } }),

  getAPUpcoming: (currency?: string) =>
    apiClient.get<FinanceLedgerList<AccountsPayable>>('/finance/ap', { params: { status: 'pending', currency, pageSize: 100 } }),
};

// ── Payment Gateway ────────────────────────────────────────────────────────────

export type PaymentGatewayProvider = 'STRIPE' | 'PAYPAL' | 'WIRE_TRANSFER';
export type PaymentGatewayStatus = 'active' | 'inactive' | 'test';
export type OnlinePaymentStatus =
  | 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED'
  | 'CANCELLED' | 'REFUNDED' | 'PARTIAL_REFUND' | 'EXPIRED';
export type OnlinePaymentType = 'DEPOSIT' | 'BALANCE' | 'FULL' | 'INVOICE';

export interface PaymentGatewayConfig {
  id: string;
  provider: PaymentGatewayProvider;
  displayName: string;
  status: PaymentGatewayStatus;
  supportedCurrencies: string[];
  minAmountCents: number;
  maxAmountCents: number;
  bankInfoJson?: Record<string, unknown> | null;
  isSandbox: boolean;
  feeBps: number;
  sortOrder: number;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OnlinePayment {
  id: string;
  salesOrderId?: string | null;
  arTransactionId?: string | null;
  customerId?: string | null;
  customerEmail?: string | null;
  customerName?: string | null;
  paymentType: OnlinePaymentType;
  provider: PaymentGatewayProvider;
  amount: number;
  currency: string;
  status: OnlinePaymentStatus;
  gatewaySessionId?: string | null;
  gatewayIntentId?: string | null;
  refundedAmount: number;
  gatewayRefundId?: string | null;
  refundReason?: string | null;
  refundedAt?: string | null;
  succeededAt?: string | null;
  failedAt?: string | null;
  failureMessage?: string | null;
  paymentLink?: string | null;
  notes?: string | null;
  proofUrl?: string | null;
  proofUploadedAt?: string | null;
  createdAt: string;
}

export interface CheckoutSessionResult {
  paymentId: string;
  provider: string;
  checkoutUrl: string;
  sessionId: string;
  paymentLink?: string;
}

export interface PaymentStats {
  succeeded_count: number;
  succeeded_volume: number;
  failed_count: number;
  pending_count: number;
  refunded_volume: number;
  stripe_count: number;
  paypal_count: number;
  wire_count: number;
}

export const paymentGatewayApi = {
  // Config
  listConfigs: () =>
    apiClient.get<{ data: PaymentGatewayConfig[] }>('/payment-gateway/configs'),

  saveConfig: (dto: Partial<PaymentGatewayConfig> & { credentials?: Record<string, string> }) =>
    apiClient.post<{ data: PaymentGatewayConfig }>('/payment-gateway/configs', dto),

  deleteConfig: (provider: PaymentGatewayProvider) =>
    apiClient.delete(`/payment-gateway/configs/${provider}`),

  getActive: () =>
    apiClient.get<{ data: PaymentGatewayConfig[] }>('/payment-gateway/active'),

  // Checkout
  createCheckout: (dto: {
    salesOrderId?: string;
    arTransactionId?: string;
    provider: PaymentGatewayProvider;
    paymentType: OnlinePaymentType;
    amount: number;
    currency: string;
    customerEmail?: string;
    customerName?: string;
    customerId?: string;
    successUrl?: string;
    cancelUrl?: string;
  }) =>
    apiClient.post<{ data: CheckoutSessionResult }>('/payment-gateway/checkout', dto),

  // Wire confirm
  confirmWire: (id: string, data: { bankReference?: string; attachmentUrl?: string }) =>
    apiClient.post(`/payment-gateway/${id}/confirm-wire`, data),

  // Payment stats for finance dashboard (backend returns raw SQL object, no { data: } wrapper)
  getStats: () =>
    apiClient.get<PaymentStats>('/payment-gateway/payments/stats'),

  // List online payments (backend returns { items, total, page, limit })
  listPayments: (params?: { page?: number; limit?: number; status?: string; provider?: string }) =>
    apiClient.get<{ items: OnlinePayment[]; total: number; page: number; limit: number }>('/payment-gateway/payments', { params }),

  // Payments by order
  getByOrder: (orderId: string) =>
    apiClient.get<OnlinePayment[]>(`/payment-gateway/payments/by-order/${orderId}`),

  // Refund
  refund: (id: string, data: { amount?: number; reason?: string }) =>
    apiClient.post<{ data: OnlinePayment }>(`/payment-gateway/${id}/refund`, data),
};


// ── M2 Supplier Extraction Pipeline (T3) ──────────────────────
export interface ExtractionJob {
  id: string;
  supplierId?: string | null;
  sourceType: string;
  sourceRef: string;
  categoryId?: string | null;
  categoryGuessed: boolean;
  status: string;
  rawText?: string | null;
  extractedJson?: Record<string, unknown> | null;
  validationJson?: Record<string, unknown> | null;
  confidence?: number | null;
  submissionId?: string | null;
  reviewNotes?: string | null;
  errorMessage?: string | null;
  createdAt: string;
}

export const extractionJobApi = {
  list: (status?: string, limit = 100) =>
    apiClient.get<{ data: { items: ExtractionJob[]; total: number } }>('/m2/extraction-jobs', { params: { status, limit } }),
  getById: (id: string) =>
    apiClient.get<{ data: ExtractionJob }>(`/m2/extraction-jobs/${id}`),
  create: (data: { sourceType: string; sourceRef: string; supplierId?: string; categoryId?: string }) =>
    apiClient.post<{ data: ExtractionJob }>('/m2/extraction-jobs', data),
  review: (id: string, body: { action: 'approve' | 'reject'; correctedJson?: Record<string, unknown>; categoryId?: string; notes?: string }) =>
    apiClient.post<{ data: ExtractionJob }>(`/m2/extraction-jobs/${id}/review`, body),
};
